/**
 * ProjectIntentForm — Site_Configuration_Consolidation SC2-3.
 *
 * Shared edit surface for `ProjectIntent` — used by:
 *   - SiteConfigurationPanel (the canonical site-level edit surface)
 *   - SiteRagEngine site-create wizard (legacy mount; SC2-3 leaves the
 *     in-wizard form in place but new code routes through this
 *     component so the next refactor can collapse the two without
 *     rewriting the chip grammar)
 *   - NewReportWizard / FeasibilityRerunPanel override editors (SC2-5
 *     and SC2-6 mount this when the user toggles "override for this
 *     report only" / "edit and apply to site")
 *
 * Visual grammar: the platform's pill-button pattern (active = filled,
 * inactive = muted-bordered), small-caps tracking-widest section
 * headers. Matches the legacy site-create wizard's Quick-profile block
 * so users get a single visual language across surfaces.
 *
 * SSOT-2 — Site Project Intent is the single source of truth (pack
 * `docs/prompt-packs/Site_SSOT_Prompt.md`). This form exposes ALL six
 * canonical fields that previously also lived on per-report
 * `*Preferences` interfaces. Coverage verified 2026-05-17:
 *   - `workloadProfile` — Section 1 picker (line ~933)
 *   - `powerRequirementMW` — exact-MW input (`mwInput`, line ~127)
 *   - `coolingStrategy` — pill grid (line ~976)
 *   - `nuclearInterest` — pill grid (line ~992)
 *   - `priority` — pill grid (line ~1017)
 *   - `inServiceDate` — exact date input (`dateInput`, line ~540)
 * SSOT-7 mounts the IC6 disagreement detector here (SiteContext drift
 * notices beneath each canonical field).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Zap,
  Cpu,
  Layers,
  Snowflake,
  CalendarClock,
  Atom,
  Target,
  Check,
  ArrowLeft,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import { Button } from '../ui/button';
import type {
  AttestationState,
  ProjectIntent,
  ProjectPhase,
  PhasePowerComponentKey,
  PhasePowerSourceConfig,
  GridConnectionMode,
  WaterSourceKind,
} from '../../types/siteRag';
import { hasDeclaredWaterSource, waterSourceKinds } from '../../lib/intent/waterSource';
import { isScreeningRecommendationMode, withScreeningIntentMode } from '../../lib/intent/screeningIntentMode';
import {
  clearIntentFieldExplicitlyUnknown,
  isIntentFieldExplicitlyUnknown,
  markIntentFieldExplicitlyUnknown,
} from '../../lib/intent/explicitUnknown';
import {
  ANNUAL_DEMAND_UTILIZATION_MAX_PCT,
  ANNUAL_DEMAND_UTILIZATION_MIN_PCT,
  annualDemandUtilizationRangePctError,
  demandUtilizationFromContextClaims,
} from '../../lib/intent/annualDemandUtilization';
import { PROJECT_INTENT_ATTESTATION_DEFS } from '../../lib/projectIntentAttestations';
import {
  PHASE_POWER_COMPONENT_LABEL,
  PHASE_POWER_COMPONENT_KEYS as CANONICAL_PHASE_POWER_COMPONENT_KEYS,
  GRID_CONNECTION_LABEL,
} from '../../types/siteRag';
import type { SiteContext } from '../../types/siteContext';
import { WorkloadProfilePicker } from './WorkloadProfilePicker';
import { WorkloadAssumptionsTable } from './WorkloadAssumptionsTable';
import { WorkloadEngineeringReference } from './WorkloadEngineeringReference';
import type { CrossFieldWarning } from './preferences/crossFieldWarnings';
import { CrossFieldWarningChip } from './preferences/CrossFieldWarning';
import {
  POWER_BUCKET_LABEL,
  POWER_BUCKET_VALUES,
  bucketFromMw,
  effectivePower,
  effectivePhases,
  makeEmptyPhase,
  withPowerRequirementMW,
  withPowerRequirementBucket,
  type PowerBucket,
  effectiveTimeline,
  withInServiceDate,
  withTimelineBucket,
  type TimelineBucket,
  effectivePhaseTimeline,
  withPhaseInServiceDate,
  withPhaseTimelineBucket,
  effectivePhaseCapacity,
  withPhaseCapacityMW,
  withPhaseCapacityBucket,
  withPhaseCapacityMax,
  withPhasePowerSourceConfig,
  effectivePhaseSourceCapacity,
  withPhaseSourceCapacityMW,
  withPhaseSourceCapacityRange,
  carryForwardPowerConfig,
  formatPhasePowerSource,
  phaseTimelineTipText,
  phaseIncrementMW,
  perSourceIncrementMW,
} from '../../lib/projectIntent';
import { disagreementsForField } from '../../lib/inputLayerWarnings';
import type { InputFieldId } from '../../lib/inputResolution';
import { SuggestionChip } from './SuggestionChip';
import type {
  IntentSuggestion,
  IntentSuggestionSource,
  CapacityCreditTargetId,
  OfftakeStructureId,
  CarbonCommitmentId,
} from '../../lib/suggestProjectIntent';
import type { SuggestedFullIntent } from '../../lib/intentIntelligence/mergeSuggestions';
import type { NextInputRecommendation, RankableIntentField } from '../../lib/intentIntelligence/nextBestInput';
import { inferPowerBasisFromText } from '../../lib/intent/powerBasisHint';
import { ElectricitySupplyField, ScreeningSharpenFields } from './intent/ScreeningSharpenFields';
import { ScreeningApproachPicker } from './intent/ScreeningApproachPicker';
import {
  REACTOR_DESIGN_CATALOG,
  REACTOR_DESIGN_CLASS_IDS,
  REACTOR_DESIGN_V1_SCOPE_NOTE,
  intentShowsReactorDesignPicker,
  type ReactorDesignClassId,
} from '../../config/reactorDesignCatalog';
import { vendorsForClass, getReactorVendor } from '../../config/reactorVendorCatalog';
import {
  COOLING_STRATEGY_LABELS,
  NUCLEAR_INTEREST_LABELS,
  TIMELINE_LABELS,
  summarizeProjectIntent,
} from '../../lib/projectIntentSummary';
// The one-line summary is pure, so it lives in lib for server use; re-exported
// for the UI modules that import it from here.
export { summarizeProjectIntent };
import {
  assessPhaseFeasibility,
  DEFAULT_CAPACITY_FACTOR_PCT,
  firmCapacitySiteContextFromIntent,
  type PhaseFeasibilitySiteContext,
} from '../../lib/phaseFeasibility';
import { isMaxPowerDerivedIntent } from '../../lib/generationImpact/maxPowerIntentStamp';
import { PhasePowerRecommendationBlock } from './PhasePowerRecommendationBlock';
import type {
  PhasePlanEntry,
  PhasePowerPlanResolution,
} from '../../lib/powerAvailability/recommendPhasePowerPlan';
import {
  formatPowerBandLayer,
  resolveIntentPowerFigure,
  resolveIntentPowerStack,
  resolvePhasePowerStack,
  isValidPue,
  isValidPowerFactor,
  sizingBasisLabel,
  DEFAULT_PUE,
  DEFAULT_POWER_FACTOR,
  DEFAULT_TD_LOSS_PCT,
  DEFAULT_POWER_BASIS,
  type PowerBandLayer,
  type PowerBasis,
  type PueSource,
} from '../../lib/powerStack';
import type { BopHeatRejection, CoolingGeneration } from '../../types/siteRag';
import {
  projectCoolingGeneration,
  coolingGenerationForSink,
  reconcileSinkForCoolingGeneration,
} from '../../types/siteRag';
import {
  coolingContextFromIntent,
  coolingOptionsFor,
  isCoolingGenerationViable,
  isHeatRejectionViable,
} from '../../lib/cooling/coolingValidity';
import { isIntentNextBestInputEnabled } from '../../lib/runtimeFlags';

const POWER_REQUIREMENT_LABELS: Record<NonNullable<ProjectIntent['powerRequirement']>, string> = POWER_BUCKET_LABEL;

const POWER_MW_MIN = 0.1;
// Exact-MW entry ceiling. The categorical bucket ladder tops at the open-high
// `over-1500mw` band, but the EXACT-MW input must still accept precise large
// loads (e.g. a 2 GW hyperscale campus) — bucketFromMw maps anything >=1500 to
// over-1500mw. Keep aligned with INFER_POWER_MAX_MW / POWER_FORM_MAX_MW (10000).
const POWER_MW_MAX = 10000;
const MAX_PLANNING_YEARS = 20;

/** UI-only sentinel; the persisted value remains the field's canonical unknown. */
const EXPLICIT_UNKNOWN_SELECT_VALUE = '__explicit_unknown__';

/**
 * BoP (balance-of-plant) heat-rejection / cold-end options for the refined
 * cooling select. The COOLING-SYSTEM side, distinct from the IT-side
 * `coolingStrategy`. `absorption-cchp` reuses on-site waste heat to drive
 * cooling thermally (lower electrical PUE). Order: most→least common, with the
 * thermal / free options and `unknown` last. See `BopHeatRejection` and
 * `resolveConfigPue` (powerStack.ts). ADDITIVE — does not alter the IT-side
 * `coolingStrategy` vocabulary or `normaliseCooling`.
 */
const BOP_HEAT_REJECTION_OPTIONS: ReadonlyArray<{ value: BopHeatRejection; label: string }> = [
  { value: 'dry-cooler',        label: 'Dry cooler — no evaporative water in verified dry mode' },
  { value: 'wet-cooling-tower', label: 'Wet cooling tower — evaporative (water)' },
  { value: 'hybrid',            label: 'Hybrid — dry + wet' },
  { value: 'once-through',      label: 'Once-through — river / sea (water)' },
  { value: 'absorption-cchp',   label: 'Absorption / CCHP cold end (waste-heat machine)' },
  { value: 'free-cooling',      label: 'Free cooling — economiser (ambient)' },
  { value: 'unknown',           label: 'Unknown / not yet decided' },
];

/**
 * Human-readable provenance label for a resolved PUE, by `PueSource`. The
 * config estimate is explicitly flagged "pending engineering simulation" so the
 * user knows it is an average estimate, not a validated figure. The simulation
 * label is templated with the phase number by the caller.
 */
function pueSourceLabel(source: PueSource): string {
  switch (source) {
    case 'config-estimate':
      return 'Average estimate — pending engineering simulation';
    case 'simulation':
      return 'Simulation-derived';
    case 'user':
      return 'User-set';
    case 'design-target':
      return 'Design target';
    case 'default':
      return 'Default';
    default: {
      // Exhaustiveness guard — a new PueSource must be labelled here.
      const _never: never = source;
      void _never;
      return 'Estimate';
    }
  }
}

/* ── Intent Intelligence — human field labels for the next-best-input nudge ──
 * Short, user-facing names for each rankable intent field, used ONLY by the
 * flag-gated "Suggested next" nudge headline. Keep aligned with the field
 * section titles above so the nudge reads in the same vocabulary as the form. */
const NEXT_INPUT_FIELD_LABEL: Record<RankableIntentField, string> = {
  workloadProfile:      'Workload profile',
  powerRequirement:     'Power requirement',
  coolingStrategy:      'IT cooling loop',
  nuclearInterest:      'Nuclear interest',
  priority:             'Priority',
  timeline:             'Timeline',
  reactorDesignClass:   'Reactor design class',
  pueValue:             'PUE',
  powerFactor:          'Power factor',
  bopHeatRejection:     'Heat rejection',
  interconnectionStatus:'Interconnection status',
  waterSource:          'Water source',
  fuelAccess:           'Fuel access',
  offtake:              'Offtake structure',
  capacityCreditIntent: 'Firm-capacity target',
  carbonCommitment:     'Carbon commitment',
};

/* Water-source multi-select options. `'none'` is intentionally absent — an
 * empty selection IS the "no source identified / undecided" state. */
const WATER_SOURCE_OPTIONS: ReadonlyArray<{ value: WaterSourceKind; label: string }> = [
  { value: 'municipal',   label: 'Municipal' },
  { value: 'groundwater', label: 'Groundwater' },
  { value: 'surface',     label: 'Surface water' },
  { value: 'reuse',       label: 'Reuse / reclaimed' },
];

/* ── #34 — DECIDE-NEXT BTM decision-field labels ──────────────────────────
 * One label map per BTM enum so the suggestion chip + inline editor render a
 * human-readable caption. Mirror the option copy in the grid-&-offtake band
 * (and the carbon-commitment select) so the two surfaces read identically. */
const CAPACITY_CREDIT_TARGET_LABELS: Record<CapacityCreditTargetId, string> = {
  'none':    'No firm-capacity sought',
  'partial': 'Partial accreditation',
  'firm':    'Full firm accreditation',
};

const OFFTAKE_STRUCTURE_LABELS: Record<OfftakeStructureId, string> = {
  'merchant':   'Merchant',
  'contracted': 'Contracted PPA',
  'hybrid':     'Hybrid',
};

const CARBON_COMMITMENT_LABELS: Record<CarbonCommitmentId, string> = {
  'none':          'None',
  're100':         'RE100',
  '24-7-cfe':      '24/7 CFE',
  'net-zero-2030': 'Net-zero 2030',
  'net-zero-2050': 'Net-zero 2050',
  'custom':        'Custom',
};

/* ── Focused-Stepper step model ───────────────────────────────────────
 * The 7 guided steps, in order. `id` keys both the active-step gate and
 * the per-step completeness predicate; `icon` is a lucide glyph. The
 * Operator-Attestations block is folded into the bottom of the
 * `priority` step (it has no step of its own). Phasing is always
 * OPTIONAL — its completeness is neutral (never gated). */
export type StepperStepId =
  | 'context'
  | 'power'
  | 'workload'
  | 'phasing'
  | 'cooling'
  | 'timeline'
  | 'nuclear'
  | 'priority';

interface StepperStep {
  id: StepperStepId;
  label: string;
  /** One-line purpose sub-text shown under the step title. */
  purpose: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPPER_STEPS: readonly StepperStep[] = [
  { id: 'power',    label: 'Power',    purpose: 'How much power the project needs, and what your MW figure means.', icon: Zap },
  { id: 'workload', label: 'Workload', purpose: 'What the facility will run, to guide power and cooling assumptions.',      icon: Cpu },
  { id: 'phasing',  label: 'Phasing',  purpose: 'Optional phases, with their capacity and target timing.',     icon: Layers },
  { id: 'cooling',  label: 'Cooling',  purpose: 'How equipment is cooled, how heat leaves the site, and water source & rights. Unknowns are accepted.',          icon: Snowflake },
  { id: 'timeline', label: 'Timeline', purpose: 'Target in-service date or planning horizon.',                       icon: CalendarClock },
  { id: 'nuclear',  label: 'Nuclear',  purpose: 'On-site nuclear interest and, if relevant, reactor design class.',  icon: Atom },
  { id: 'priority', label: 'Priority', purpose: 'Your priorities and the assumptions you can confirm.',       icon: Target },
] as const;

/**
 * Declutter (2026-06-10) — the optional Context step, PREPENDED as step 1
 * ONLY when the host hands the form a `contextStep` node (the silent
 * SiteContextInput element). Defined separately from STEPPER_STEPS so the
 * canonical 7 field steps are untouched for callers that don't fold
 * context in (wizard / rerun / parity tests still see exactly 7 steps).
 * Its completeness tick reads the host-supplied `contextStepComplete`
 * flag (context text non-empty); it is best-effort and never blocks
 * navigation.
 */
const CONTEXT_STEP: StepperStep = {
  id: 'context',
  label: 'Context',
  purpose: 'What we know about this site — auto-captured as evidence.',
  icon: BookOpen,
};

/**
 * Per-step completeness predicate, read from the REAL `ProjectIntent`
 * fields (never a separate flag). Drives the rail tick + the `N/7
 * complete` count + progress bar. `phasing` is always optional and
 * reads `true` so it never blocks the count — staged rollout is a
 * refinement, not a required input. `mixed` workload is the unset
 * default, so it does NOT tick (but remains a valid value). */
/** The explicit-unknown key each stepper step's own field is stamped with. */
const STEPPER_STEP_UNKNOWN_FIELD: Partial<Record<StepperStepId, string>> = {
  power: 'powerRequirement',
  workload: 'workloadProfile',
  cooling: 'coolingStrategy',
  timeline: 'timeline',
  nuclear: 'nuclearInterest',
  priority: 'priority',
};

function stepperStepIsComplete(id: StepperStepId, intent: ProjectIntent): boolean {
  // A step whose field the user recorded as unknown is ANSWERED: tick it, so
  // the rail stops asking. The value itself stays unknown.
  const unknownField = STEPPER_STEP_UNKNOWN_FIELD[id];
  if (unknownField && isIntentFieldExplicitlyUnknown(intent, unknownField)) return true;
  switch (id) {
    case 'context':
      // Context completeness is host-driven (the prose text lives in the
      // passed node, not in `intent`), so the stepper render resolves it
      // from `contextStepComplete` and never calls this for 'context'.
      // Treated as neutral-complete here so it never blocks the count.
      return true;
    case 'power':
      return (typeof intent.powerRequirementMW === 'number' && intent.powerRequirementMW > 0)
        || (intent.powerRequirement != null && intent.powerRequirement !== 'undecided');
    case 'workload':
      return intent.workloadProfile != null && intent.workloadProfile !== 'mixed';
    case 'phasing':
      // Always optional — neutral / never-blocking.
      return true;
    case 'cooling':
      return intent.coolingStrategy != null && intent.coolingStrategy !== 'undecided';
    case 'timeline':
      return Boolean(intent.inServiceDate) || (intent.timeline != null && intent.timeline !== 'undecided');
    case 'nuclear':
      return intent.nuclearInterest != null && intent.nuclearInterest !== 'undecided';
    case 'priority':
      return intent.priority != null && intent.priority !== 'undecided';
    default: {
      const _never: never = id;
      void _never;
      return false;
    }
  }
}

const PillButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
  disabled?: boolean;
}> = ({ active, onClick, children, testId, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    disabled={disabled}
    aria-pressed={active}
    className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border rounded-md transition-colors ${
      active
        ? 'selected-accent'
        : 'border-border-subtle text-text-secondary hover:border-border hover:text-text-primary'
    } ${disabled ? 'opacity-50 cursor-not-allowed hover:border-border-subtle hover:text-text-secondary' : ''}`}
  >
    {children}
  </button>
);

/* ── PowerRequirementField — dual-mode Exact MW / Range bucket input ───
 *
 * Project_Intent_Exact_MW_Prompt PM2. Replaces the legacy "pick a bucket"
 * pill row with a mode toggle:
 *
 *   ○ Exact MW  ● Range bucket
 *
 * In Exact mode the number input is editable; the range pills are
 * disabled but highlighted to show the derived bucket via `bucketFromMw`
 * so users can sanity-check which categorical class their number lands
 * in. In Range mode the pills behave like the legacy form and the MW
 * field is cleared so future reads via `effectivePower()` report
 * `isExact: false`.
 *
 * Validation: MW must be > 0 and ≤ 10,000 (sanity bound). Inline error
 * shown below the input when out of range. The error doesn't block
 * onChange — partial / invalid input still updates the draft so the
 * "Save" button can stay disabled at the panel level if needed; the
 * write itself happens only when the user picks Save. We keep the
 * intent's stored bucket/MW consistent regardless of the error state.
 */
interface PowerRequirementFieldProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
}

const PowerRequirementField: React.FC<PowerRequirementFieldProps> = ({ intent, onChange, testIdPrefix }) => {
  const hasExact = typeof intent.powerRequirementMW === 'number' && intent.powerRequirementMW > 0;
  // Mode is purely UI state — the intent never carries both modes
  // simultaneously, so the source of truth on first render is whether
  // `powerRequirementMW` is populated.
  const [mode, setMode] = useState<'exact' | 'range'>(hasExact ? 'exact' : 'range');
  // The number input mirrors `intent.powerRequirementMW` as a string so
  // users can type intermediate states ("3.", "", "0.5") without us
  // round-tripping through Number() on every keystroke. Empty string =
  // "user wiped the field" — we hold the draft until they leave the
  // input or type a valid number.
  const [mwInput, setMwInput] = useState<string>(hasExact ? String(intent.powerRequirementMW) : '');

  // Re-sync when the parent passes a different intent (e.g. user
  // switched sites, Cancel reverts the draft). Cheap because the
  // identity comparison short-circuits the common case.
  useEffect(() => {
    if (hasExact) {
      setMode('exact');
      setMwInput(String(intent.powerRequirementMW));
    } else if (mode === 'exact') {
      // Parent cleared MW from under us — fall back to range. Don't
      // touch mode when the user picked range explicitly.
      setMode('range');
      setMwInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.powerRequirementMW]);

  const parsedMw = useMemo(() => {
    if (mwInput.trim() === '') return null;
    const n = Number(mwInput);
    return Number.isFinite(n) ? n : null;
  }, [mwInput]);

  const validationError = useMemo(() => {
    if (mode !== 'exact') return null;
    if (mwInput.trim() === '') return null;
    if (parsedMw == null) return 'MW must be a number.';
    if (parsedMw <= 0) return 'MW must be greater than 0.';
    if (parsedMw > POWER_MW_MAX) return `MW seems too large (max ${POWER_MW_MAX.toLocaleString('en-US')} MW).`;
    return null;
  }, [mode, mwInput, parsedMw]);

  const derivedBucket: PowerBucket = parsedMw != null && parsedMw > 0
    ? bucketFromMw(parsedMw)
    : intent.powerRequirement;

  const switchToExact = () => {
    if (mode === 'exact') return;
    setMode('exact');
    // Don't write to the intent yet — wait until the user types a
    // valid number. The toggle itself shouldn't dirty the form.
  };

  const switchToRange = () => {
    if (mode === 'range') return;
    setMode('range');
    setMwInput('');
    if (hasExact) {
      // Clear MW so effectivePower() drops back to bucket-only on the
      // next read. Keep the current bucket so the user doesn't lose
      // their categorical choice.
      onChange(clearIntentFieldExplicitlyUnknown(
        withPowerRequirementBucket(intent, intent.powerRequirement),
        'powerRequirement',
      ));
    }
  };

  const handleMwInput = (raw: string) => {
    setMwInput(raw);
    const n = Number(raw);
    if (raw.trim() !== '' && Number.isFinite(n) && n > 0 && n <= POWER_MW_MAX) {
      onChange(clearIntentFieldExplicitlyUnknown(
        withPowerRequirementMW(intent, n),
        'powerRequirement',
      ));
    } else if (raw.trim() === '' && hasExact) {
      // User wiped the field — drop the MW so we don't keep stale data
      // on Save. Keep the last-known bucket.
      onChange(withPowerRequirementBucket(intent, intent.powerRequirement));
    }
  };

  const handleBucketClick = (b: PowerBucket) => {
    if (mode !== 'range') return;
    const next = withPowerRequirementBucket(intent, b);
    onChange(b === 'undecided'
      ? markIntentFieldExplicitlyUnknown(next, 'powerRequirement')
      : clearIntentFieldExplicitlyUnknown(next, 'powerRequirement'));
  };

  return (
    <div className="space-y-1.5">
      <div className="type-meta text-text-muted">Power requirement (MW)</div>
      <p id={`${testIdPrefix}-power-requirement-help`} className="text-[11px] leading-relaxed text-text-muted">
        How much electrical demand should the project serve? Choose the meaning of this quantity under Power basis below.
        {' '}Leave it unknown when screening should recommend a size.
      </p>
      <div className="flex flex-wrap items-center gap-3" role="radiogroup" aria-label="Power requirement entry mode">
        <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-power-mode`}
            checked={mode === 'exact'}
            onChange={switchToExact}
            data-testid={`${testIdPrefix}-power-mode-exact`}
            className="accent-text-primary"
          />
          Exact MW
        </label>
        <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-power-mode`}
            checked={mode === 'range'}
            onChange={switchToRange}
            data-testid={`${testIdPrefix}-power-mode-range`}
            className="accent-text-primary"
          />
          Range bucket
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-stretch border border-border-subtle rounded-md overflow-hidden">
          <input
            type="number"
            inputMode="decimal"
            min={POWER_MW_MIN}
            max={POWER_MW_MAX}
            step={0.1}
            placeholder="e.g. 350"
            value={mwInput}
            disabled={mode !== 'exact'}
            onChange={(e) => handleMwInput(e.target.value)}
            aria-label="Exact MW"
            aria-describedby={`${testIdPrefix}-power-requirement-help`}
            aria-invalid={validationError != null}
            data-testid={`${testIdPrefix}-power-mw-input`}
            className={`w-28 px-2 py-1.5 text-[12px] bg-canvas text-text-primary outline-none focus:ring-1 focus:ring-primary disabled:bg-state-hover disabled:text-text-muted ${
              validationError ? 'ring-1 ring-danger' : ''
            }`}
          />
          <span className="inline-flex items-center px-2 type-meta text-text-muted border-l border-border-subtle bg-state-hover">
            MW
          </span>
        </div>

        <div className="flex flex-wrap gap-2" aria-disabled={mode !== 'range'}>
          {POWER_BUCKET_VALUES.map(p => {
            const isActive = mode === 'range'
              ? intent.powerRequirement === p
              : derivedBucket === p && parsedMw != null && parsedMw > 0;
            return (
              <PillButton
                key={p}
                active={isActive}
                onClick={() => handleBucketClick(p)}
                testId={`${testIdPrefix}-power-${p}`}
                disabled={mode !== 'range'}
              >
                {POWER_REQUIREMENT_LABELS[p]}
              </PillButton>
            );
          })}
        </div>
      </div>

      {validationError && (
        <div
          className="text-[10px] text-danger"
          role="alert"
          data-testid={`${testIdPrefix}-power-mw-error`}
        >
          {validationError}
        </div>
      )}

      {isMaxPowerDerivedIntent(intent) && intent.maxPowerStudy && (
        <div
          className="rounded-md border border-border-subtle bg-state-hover/40 p-2 text-[10px] leading-relaxed text-text-secondary"
          data-testid="power-max-power-derived"
        >
          <b className="text-text-primary">Derived from the max-power study.</b>{' '}
          {intent.maxPowerStudy.boundQuality === 'search-ceiling-reached'
            ? 'No documented constraint bound the site, so this is a floor ("at least"), not a ceiling.'
            : intent.maxPowerStudy.boundQuality === 'upper-bound-undocumented-constraints'
              ? 'At least one constraint is undocumented, so this is an UPPER BOUND — the true figure can only be lower.'
              : 'Bounded by the constraints documented for this site.'}
          {' '}Firm generation {Math.round(intent.maxPowerStudy.firmNetMW)} MW → {Math.round(intent.maxPowerStudy.facilityMW)} MW facility.
          {' '}Editing the value here replaces the derived figure with a declared one.
        </div>
      )}

      <div className="text-[10px] italic text-text-muted leading-relaxed">
        Enter exact MW if you know it — reports cite the precise number in LCOE calculations and narrative.
        Use a range only when sizing isn't decided yet.
      </div>
    </div>
  );
};

/* ── Annual demand utilization — DATA-CENTER load, never generation CF ── */

interface AnnualDemandUtilizationFieldProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
  siteContext?: SiteContext | null;
}

const AnnualDemandUtilizationField: React.FC<AnnualDemandUtilizationFieldProps> = ({
  intent,
  onChange,
  testIdPrefix,
  siteContext,
}) => {
  const stored = intent.annualDemandUtilizationRangePct;
  const explicitlyUnknown = isIntentFieldExplicitlyUnknown(
    intent,
    'annualDemandUtilizationRangePct',
  );
  const knownStored = explicitlyUnknown ? undefined : stored;
  const [lowInput, setLowInput] = useState(knownStored ? String(knownStored.low) : '');
  const [highInput, setHighInput] = useState(knownStored ? String(knownStored.high) : '');
  const localWrite = useRef(false);
  const contextResolution = useMemo(
    () => demandUtilizationFromContextClaims(siteContext?.claims),
    [siteContext?.claims],
  );

  // Follow site changes, Cancel, and external suggestion/adoption writes. A
  // local write already owns the string drafts, including invalid intermediate
  // states, so do not erase them on the immediate parent echo.
  useEffect(() => {
    if (localWrite.current) {
      localWrite.current = false;
      return;
    }
    setLowInput(knownStored ? String(knownStored.low) : '');
    setHighInput(knownStored ? String(knownStored.high) : '');
  }, [knownStored?.low, knownStored?.high, explicitlyUnknown]);

  const parsed = useMemo(() => {
    const low = lowInput.trim() === '' ? undefined : Number(lowInput);
    const high = highInput.trim() === '' ? undefined : Number(highInput);
    return { low, high };
  }, [lowInput, highInput]);

  const validationError = useMemo(() => {
    if (explicitlyUnknown || (parsed.low == null && parsed.high == null)) return null;
    return annualDemandUtilizationRangePctError(parsed);
  }, [explicitlyUnknown, parsed]);

  const commitDraft = (nextLow: string, nextHigh: string) => {
    const low = nextLow.trim() === '' ? undefined : Number(nextLow);
    const high = nextHigh.trim() === '' ? undefined : Number(nextHigh);
    const candidate = { low, high };
    const valid = annualDemandUtilizationRangePctError(candidate) == null
      && low != null
      && high != null;
    localWrite.current = true;
    onChange(clearIntentFieldExplicitlyUnknown({
      ...intent,
      annualDemandUtilizationRangePct: valid ? { low, high } : undefined,
    }, 'annualDemandUtilizationRangePct'));
  };

  const setUnknown = () => {
    setLowInput('');
    setHighInput('');
    localWrite.current = true;
    onChange(markIntentFieldExplicitlyUnknown({
      ...intent,
      annualDemandUtilizationRangePct: undefined,
    }, 'annualDemandUtilizationRangePct'));
  };

  const clearUnknown = () => {
    localWrite.current = true;
    onChange(clearIntentFieldExplicitlyUnknown({
      ...intent,
      // A malformed imported record can carry both a value and the explicit
      // Unknown marker. Clearing the marker returns to truly unanswered; it
      // must not silently adopt the stale value.
      annualDemandUtilizationRangePct: undefined,
    }, 'annualDemandUtilizationRangePct'));
  };

  const adoptContextLoadFactor = () => {
    if (!contextResolution) return;
    const { low, high } = contextResolution.range;
    setLowInput(String(low));
    setHighInput(String(high));
    localWrite.current = true;
    onChange(stampAccepted(clearIntentFieldExplicitlyUnknown({
      ...intent,
      annualDemandUtilizationRangePct: { low, high },
    }, 'annualDemandUtilizationRangePct'), 'annualDemandUtilizationRangePct'));
  };

  return (
    <div
      className="mt-3 space-y-1.5 rounded-md border border-border-subtle p-2.5"
      data-field-id="annualDemandUtilizationRangePct"
      data-testid={`${testIdPrefix}-annual-demand-utilization`}
      data-answer-state={explicitlyUnknown ? 'explicit-unknown' : knownStored ? 'recorded' : 'unanswered'}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="type-meta text-text-muted">Annual data-center demand utilization / load factor</div>
          <p className="mt-0.5 text-[10px] leading-relaxed text-text-muted">
            Average annual data-center demand ÷ the MW ceiling above. This is not a generator capacity factor.
          </p>
        </div>
        <button
          type="button"
          aria-pressed={explicitlyUnknown}
          onClick={explicitlyUnknown ? clearUnknown : setUnknown}
          data-testid={`${testIdPrefix}-annual-demand-utilization-unknown`}
          className={`rounded-md border px-2 py-1 type-meta font-bold transition-colors ${
            explicitlyUnknown
              ? 'border-warning/50 bg-warning/10 text-warning'
              : 'border-border-subtle text-text-muted hover:text-text-secondary'
          }`}
        >
          Unknown / not yet decided
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 type-meta text-text-muted">
          <span className="block">Low</span>
          <span className="inline-flex items-stretch overflow-hidden rounded-md border border-border-subtle">
            <input
              type="number"
              inputMode="decimal"
              min={ANNUAL_DEMAND_UTILIZATION_MIN_PCT}
              max={ANNUAL_DEMAND_UTILIZATION_MAX_PCT}
              step={0.1}
              value={lowInput}
              disabled={explicitlyUnknown}
              onChange={(event) => {
                const raw = event.target.value;
                setLowInput(raw);
                commitDraft(raw, highInput);
              }}
              aria-label="Annual demand utilization low percent"
              aria-invalid={validationError != null}
              data-testid={`${testIdPrefix}-annual-demand-utilization-low`}
              className="w-20 bg-canvas px-2 py-1 text-[12px] normal-case tracking-normal text-text-primary outline-none disabled:bg-state-hover disabled:text-text-muted"
            />
            <span className="inline-flex items-center border-l border-border-subtle bg-state-hover px-2">%</span>
          </span>
        </label>
        <span className="pb-1.5 text-text-muted" aria-hidden="true">–</span>
        <label className="space-y-1 type-meta text-text-muted">
          <span className="block">High</span>
          <span className="inline-flex items-stretch overflow-hidden rounded-md border border-border-subtle">
            <input
              type="number"
              inputMode="decimal"
              min={ANNUAL_DEMAND_UTILIZATION_MIN_PCT}
              max={ANNUAL_DEMAND_UTILIZATION_MAX_PCT}
              step={0.1}
              value={highInput}
              disabled={explicitlyUnknown}
              onChange={(event) => {
                const raw = event.target.value;
                setHighInput(raw);
                commitDraft(lowInput, raw);
              }}
              aria-label="Annual demand utilization high percent"
              aria-invalid={validationError != null}
              data-testid={`${testIdPrefix}-annual-demand-utilization-high`}
              className="w-20 bg-canvas px-2 py-1 text-[12px] normal-case tracking-normal text-text-primary outline-none disabled:bg-state-hover disabled:text-text-muted"
            />
            <span className="inline-flex items-center border-l border-border-subtle bg-state-hover px-2">%</span>
          </span>
        </label>
        <span className="pb-1 type-meta normal-case tracking-normal text-text-muted">
          Use equal endpoints for an exact load factor.
        </span>
      </div>
      {validationError && (
        <p
          className="text-[10px] text-danger"
          role="alert"
          data-testid={`${testIdPrefix}-annual-demand-utilization-error`}
        >
          {validationError}
        </p>
      )}
      {!knownStored && !explicitlyUnknown && contextResolution && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/25 bg-primary/5 px-2.5 py-2"
          data-testid={`${testIdPrefix}-annual-demand-utilization-context-suggestion`}
        >
          <p className="text-[10px] leading-relaxed text-text-secondary">
            Site Context records {contextResolution.range.low}% from uploaded project documents. Review and save it as the Intent load factor.
          </p>
          <button
            type="button"
            onClick={adoptContextLoadFactor}
            className="rounded-md border border-primary/35 bg-surface px-2 py-1 type-meta font-bold text-primary hover:bg-primary/10"
          >
            Use {contextResolution.range.low}%
          </button>
        </div>
      )}
      {explicitlyUnknown && (
        <p
          className="text-[10px] leading-relaxed text-warning"
          data-testid={`${testIdPrefix}-annual-demand-utilization-recommendation`}
        >
          Unknown recorded. Confirm the expected annual demand profile with the operator; water screening will disclose its default sensitivity until then.
        </p>
      )}
    </div>
  );
};

/* ── PowerBasisAwarenessFields — R-FIN-POWER-STACK user-awareness UI ───
 *
 * A datacenter's "MW" is ambiguous until you state the BASIS. This block
 * sits directly beneath the MW input so the user ALWAYS knows what their
 * number means and sees every derived layer of the power stack:
 *
 *   IT / critical load ──×PUE──▶ Total facility ≈ Grid interconnection
 *                                       │
 *                                       ├──÷PF──▶ Apparent power (MVA)
 *                                       └──÷(1−T&D)──▶ Generation required
 *
 * The entered MW is NEVER mutated — the basis just tells the resolver which
 * layer it sits on (default 'facility' = total grid demand). All arithmetic
 * is delegated to `resolveIntentPowerStack` (the single source of truth in
 * powerStack.ts) so the form never re-derives the physics.
 *
 * Design tokens only — no raw hex. Reuses the form's mono / small-caps
 * terminal grammar (PillButton, type-meta, border-border-subtle).
 */
interface PowerBasisAwarenessFieldsProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
  screening?: boolean;
}

/**
 * Format a resolved power-stack figure for display. PUE / PF are sub-unit
 * ratios so they keep up to two decimals (1.3, 0.95); MW / MVA figures are
 * planning-grade and round to the nearest integer (230.8 → "231"), matching
 * the Overview card's `Math.round` so the two surfaces never disagree.
 */
function formatStackNum(v: number): string {
  if (v > 0 && v < 10) {
    // Sub-10 values (PUE 1.3, PF 0.95, small IT loads) keep 2-decimal detail,
    // trimming trailing zeros (1.30 → "1.3", 1.00 → "1").
    const rounded = Math.round(v * 100) / 100;
    return Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded);
  }
  return String(Math.round(v));
}

const PowerBasisAwarenessFields: React.FC<PowerBasisAwarenessFieldsProps> = ({
  intent, onChange, testIdPrefix, screening = false,
}) => {
  const basis: PowerBasis = intent.powerBasis ?? DEFAULT_POWER_BASIS;

  // The full resolved stack — every derived layer is null when no usable MW
  // has been entered, so the breakdown is suppressed rather than showing zeros.
  // `stack.pueSource` carries the provenance of the resolved PUE so the UI can
  // label it (config-estimate vs user vs simulation), single source of truth.
  const stack = useMemo(() => resolveIntentPowerStack(intent), [intent]);
  // The shared stack includes a modeled PF default. Only a recorded, valid PF
  // can establish the apparent-power figure shown beside the user's inputs.
  const hasRecordedPowerFactor = isValidPowerFactor(intent.powerFactor)
    && !isIntentFieldExplicitlyUnknown(intent, 'powerFactor');

  // Phases that carry an engineering-sim PUE the user could ADOPT. The sim
  // writes `simulatedPueValue`; the user opts in via `adoptSimulatedPue`. We
  // surface a per-phase toggle so the adopt is user-controlled, never silent.
  const phasesWithSimPue = useMemo(
    () =>
      effectivePhases(intent).phases
        .map((phase, idx) => ({ phase, displayIndex: idx + 1 }))
        .filter(({ phase }) =>
          typeof phase.simulatedPueValue === 'number' &&
          Number.isFinite(phase.simulatedPueValue) &&
          phase.simulatedPueValue > 0,
        ),
    [intent],
  );

  // Whether the resolved config estimate earned the BTM waste-heat credit — the
  // resolved estimate sits BELOW the un-credited base only when the credit
  // applied, so detect it by comparing the credited estimate to the base. We
  // only annotate this when the PUE provenance is the config estimate.
  const wasteHeatLowered = useMemo(() => {
    if (stack.pueSource !== 'config-estimate') return false;
    // Re-resolve without the credit by clearing BOTH cooling axes that can earn
    // it — the cold-end thermal SINK (`bopHeatRejection`) AND the explicit
    // cold-production MACHINE (`coolingGeneration`). Since the cooling axes are
    // now co-written (picking `absorption-cchp` also sets `absorption-thermal`),
    // clearing only the sink would leave the explicit machine still earning the
    // credit and falsely report no lowering. If the credited estimate is
    // meaningfully lower than this un-credited base, the credit was applied.
    const baseStack = resolveIntentPowerStack({
      ...intent,
      bopHeatRejection: 'unknown',
      coolingGeneration: undefined,
    });
    return stack.pue < baseStack.pue - 1e-6;
  }, [intent, stack.pueSource, stack.pue]);

  const setBasis = (next: PowerBasis) => {
    if (intent.powerBasis === next && !isIntentFieldExplicitlyUnknown(intent, 'powerBasis')) return;
    onChange(clearIntentFieldExplicitlyUnknown({ ...intent, powerBasis: next }, 'powerBasis'));
  };

  // ONE PUE input. The separate "Target PUE" (`pueTarget`) input was removed
  // (intent-input audit): it fed the SAME resolved PUE as `pueValue`, one step
  // lower in `resolvePueWithSource`. A legacy persisted `pueTarget` is shown in
  // this box (below) and MIGRATED on the first edit — every write here drops
  // it, so the resolved PUE never silently changes and never becomes hidden,
  // uneditable state.
  const handlePue = (raw: string) => {
    if (raw.trim() === '') {
      onChange({ ...intent, pueValue: undefined, pueTarget: undefined });
      return;
    }
    const n = Number(raw);
    const pueValue = Number.isFinite(n) && n > 0 ? n : undefined;
    const next = { ...intent, pueValue, pueTarget: undefined };
    onChange(pueValue == null
      ? next
      : clearIntentFieldExplicitlyUnknown(next, 'pueValue'));
  };

  const handlePf = (raw: string) => {
    if (raw.trim() === '') {
      onChange({ ...intent, powerFactor: undefined });
      return;
    }
    const n = Number(raw);
    const powerFactor = Number.isFinite(n) && n > 0 ? n : undefined;
    const next = { ...intent, powerFactor };
    onChange(powerFactor == null
      ? next
      : clearIntentFieldExplicitlyUnknown(next, 'powerFactor'));
  };

  // NOTE: the BoP heat-rejection + BTM cooling-generation handlers and their
  // R-COOL-VALIDITY gating / stale-warning state were relocated to
  // `CoolingAxesSection` (the single clustered Cooling section below the phases
  // editor). They depended only on `intent` + `onChange`, so the move is a pure
  // restructure — no behaviour change.

  /** Toggle a phase's `adoptSimulatedPue` flag (user-controlled, not silent). */
  const toggleAdoptSimPue = (phaseId: string, next: boolean) => {
    const phases = (intent.phases ?? []).map(p =>
      p.id === phaseId ? { ...p, adoptSimulatedPue: next } : p,
    );
    onChange({ ...intent, phases });
  };

  // EXACT vs BUCKET-DERIVED vs UNDECIDED. A bucket used to fall straight through
  // to the empty state, hiding IT / facility / grid / MVA / generation-required
  // all at once — even though finance, BoM and firm-capacity were already sizing
  // off the bucket's midpoint behind the scenes. The band surfaces both the range
  // and that previously-invisible sizing basis.
  const figure = useMemo(() => resolveIntentPowerFigure(intent), [intent]);
  const band = figure.kind === 'band' ? figure.band : null;

  // Conversion-direction hints so the user sees WHY the derived figure differs
  // from what they typed. On the facility basis the entered figure is the
  // larger facility number and IT is derived down (÷PUE); on the IT-load basis
  // the entered figure is the smaller IT number and facility is derived up
  // (×PUE). Either way the LARGER facility/grid figure is what generation +
  // feasibility size against.
  const itHint = basis === 'facility' ? '= entered ÷ PUE' : 'entered';
  const facilityHint = basis === 'facility' ? 'entered' : '= entered × PUE';

  return (
    <div className="space-y-2 mt-2" data-testid={`${testIdPrefix}-power-basis`}>
      {/* Basis segmented toggle */}
      <div className="space-y-1.5" data-field-id="powerBasis">
        <div
          className="type-meta text-text-muted"
          title="Choose whether the entered MW covers IT equipment only or the whole facility."
        >
          Power basis
        </div>
        <p id={`${testIdPrefix}-power-basis-help`} className="text-[11px] leading-relaxed text-text-muted">
          IT load covers servers and other IT equipment. Total facility demand includes IT, cooling and electrical overhead.
          {' '}It describes the load to serve; the grid connection and on-site generation are separate supply choices.
        </p>
        <div
          className="inline-flex border border-border-subtle rounded-md overflow-hidden"
          role="radiogroup"
          aria-label="Power basis"
          aria-describedby={`${testIdPrefix}-power-basis-help`}
        >
          <button
            type="button"
            onClick={() => setBasis('facility')}
            aria-pressed={intent.powerBasis === 'facility'}
            data-testid={`${testIdPrefix}-power-basis-facility`}
            title="Whole-facility demand, including IT, cooling and electrical overhead. Do not add PUE again."
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold transition-colors ${
              intent.powerBasis === 'facility'
                ? 'selected-accent'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Total facility demand
          </button>
          <button
            type="button"
            onClick={() => setBasis('it-load')}
            aria-pressed={intent.powerBasis === 'it-load'}
            data-testid={`${testIdPrefix}-power-basis-itload`}
            title="IT equipment demand only. Facility demand is this MW value multiplied by the resolved PUE."
            className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border-l border-border-subtle transition-colors ${
              intent.powerBasis === 'it-load'
                ? 'selected-accent'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            IT equipment load
          </button>
        </div>
        {intent.powerBasis == null ? (
          <p className="text-[10px] leading-relaxed text-warning" data-testid={`${testIdPrefix}-power-basis-unset`}>
            {screening
              ? 'Not recorded. Screening reports compare the possible MW interpretations until the boundary is confirmed.'
              : 'Not recorded. Calculations currently use a disclosed facility-demand screening default.'}
          </p>
        ) : null}
      </div>

      {screening ? (
        <p className="text-[11px] text-text-muted" data-testid={`${testIdPrefix}-screening-assumptions`}>
          Screening reports use recorded PUE and power factor when available and disclose modeled assumptions or ranges otherwise.
          {' '}Refine these in Design &amp; report assumptions when known.
        </p>
      ) : <>
      {/* PUE + power-factor compact inputs */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1" data-field-id="pueValue">
          <span
            className="type-meta text-text-muted block"
            title="PUE = total facility demand ÷ IT load."
          >
            PUE
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={1}
            step={0.01}
            placeholder={String(DEFAULT_PUE)}
            value={intent.pueValue ?? intent.pueTarget ?? ''}
            onChange={(e) => handlePue(e.target.value)}
            aria-label="Power usage effectiveness (PUE)"
            aria-describedby={`${testIdPrefix}-power-pue-help`}
            aria-invalid={(intent.pueValue ?? intent.pueTarget) != null && !isValidPue(intent.pueValue ?? intent.pueTarget)}
            data-testid={`${testIdPrefix}-power-pue`}
            className="w-20 px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal outline-none focus:ring-1 focus:ring-primary rounded-md"
          />
        </label>
        <label className="space-y-1" data-field-id="powerFactor">
          <span
            className="type-meta text-text-muted block"
            title="real MW ÷ apparent MVA; modern UPS 0.9–1.0"
          >
            Power factor
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0.1}
            max={1}
            step={0.01}
            placeholder={String(DEFAULT_POWER_FACTOR)}
            value={intent.powerFactor ?? ''}
            onChange={(e) => handlePf(e.target.value)}
            aria-label="Power factor"
            aria-invalid={intent.powerFactor != null && !isValidPowerFactor(intent.powerFactor)}
            data-testid={`${testIdPrefix}-power-pf`}
            className="w-20 px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal outline-none focus:ring-1 focus:ring-primary rounded-md"
          />
        </label>
      </div>

      <p id={`${testIdPrefix}-power-pue-help`} className="text-[11px] leading-relaxed text-text-muted">
        PUE converts IT MW to facility MW once: IT MW × PUE = facility MW.
        {' '}If you entered facility MW, that value already includes overhead; IT MW = facility MW ÷ PUE.
      </p>

      {(intent.pueValue ?? intent.pueTarget) != null && !isValidPue(intent.pueValue ?? intent.pueTarget) && (
        <p role="alert" className="text-[11px] text-warning">PUE must be at least 1. This value is excluded from calculations; the resolved assumption is shown below.</p>
      )}
      {intent.powerFactor != null && !isValidPowerFactor(intent.powerFactor) && (
        <p role="alert" className="text-[11px] text-warning">Power factor must be greater than 0 and no greater than 1. Apparent power is unavailable until this is corrected.</p>
      )}

      {/* The BoP heat-rejection (cold end) + BTM cooling-generation selectors
          used to live here; they were relocated into the single clustered
          Cooling section (`CoolingAxesSection`) that renders AFTER the phases
          editor, alongside the IT-side cooling strategy. See CHANGE 3/4. */}

      {/* Effective PUE provenance — the resolved PUE + WHERE it came from, so the
          user knows whether it is a validated figure or an average estimate. */}
      <div
        className="flex items-baseline justify-between gap-2 border-t border-border-subtle pt-2"
        data-testid={`${testIdPrefix}-pue-provenance`}
      >
        <span className="type-meta text-text-muted">Effective PUE</span>
        <span className="text-right">
          <span className="text-[12px] tabular-nums font-bold text-text-primary mr-2">
            {formatStackNum(stack.pue)}
          </span>
          <span
            className="type-meta text-text-secondary normal-case tracking-normal"
            data-testid={`${testIdPrefix}-pue-source`}
          >
            {pueSourceLabel(stack.pueSource)}
          </span>
        </span>
      </div>

      {/* Per-phase ADOPT toggle — surfaces an engineering-sim PUE the user can
          opt into. User-controlled, never silent (R-FIN decision 3). */}
      {phasesWithSimPue.map(({ phase, displayIndex }) => (
        <label
          key={phase.id}
          className="flex items-start gap-2 border border-border-subtle bg-state-hover/40 p-2 cursor-pointer rounded-lg"
          data-testid={`${testIdPrefix}-adopt-sim-pue-${phase.id}`}
        >
          <input
            type="checkbox"
            checked={phase.adoptSimulatedPue === true}
            onChange={(e) => toggleAdoptSimPue(phase.id, e.target.checked)}
            aria-label={`Adopt simulation PUE for ${phase.label}`}
            data-testid={`${testIdPrefix}-adopt-sim-pue-toggle-${phase.id}`}
            className="mt-0.5 accent-primary"
          />
          <span className="type-meta text-text-secondary normal-case tracking-normal leading-relaxed">
            Adopt simulation PUE ({formatStackNum(phase.simulatedPueValue as number)}) for{' '}
            {phase.label || `Phase ${displayIndex}`} → updates intent + SED + reports
          </span>
        </label>
      ))}

      {/* Live power breakdown — every derived layer, each labelled with its UNIT */}
      {figure.kind === 'exact' ? (
        <div
          className="border border-border-subtle bg-state-hover/40 p-2 space-y-1 rounded-lg"
          data-testid="power-breakdown"
          data-figure-basis="exact"
        >
          <div className="type-meta text-text-muted font-bold">
            Power breakdown
            <span className="normal-case tracking-normal text-text-secondary ml-2">
              (PUE {formatStackNum(stack.pue)} · PF {hasRecordedPowerFactor ? formatStackNum(stack.powerFactor) : 'not set'})
            </span>
          </div>
          <div
            className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal"
            data-testid="power-breakdown-pue-source"
          >
            PUE source: {pueSourceLabel(stack.pueSource)}
            {wasteHeatLowered && ' — lowered by BTM waste-heat recovery'}
          </div>
          <dl className="space-y-0.5">
            <PowerStackRow
              label="IT / critical load"
              value={stack.itLoadMW}
              unit="MW"
              hint={itHint}
              testId="power-breakdown-it"
            />
            <PowerStackRow
              label="Total facility"
              value={stack.facilityMW}
              unit="MW"
              hint={facilityHint}
              emphasis
              testId="power-breakdown-facility"
            />
            <PowerStackRow
              label="Grid interconnection"
              value={stack.gridMW}
              unit="MW"
              hint="≈ facility peak"
              testId="power-breakdown-grid"
            />
            <PowerStackRow
              label="Apparent power"
              value={hasRecordedPowerFactor ? stack.apparentMVA : null}
              unit="MVA"
              hint="÷ PF"
              unresolvedHint="Set power factor to calculate MVA"
              testId="power-breakdown-apparent"
            />
            <PowerStackRow
              label="Generation required"
              value={stack.generationRequiredMW}
              unit="MW"
              hint={`÷ (1 − ${DEFAULT_TD_LOSS_PCT}% T&D)`}
              testId="power-breakdown-generation"
            />
          </dl>
          <div className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal">
            {basis === 'facility'
              ? 'You entered the FACILITY load; IT is derived (÷ PUE). The facility / grid figure is what generation + feasibility size against.'
              : 'You entered the IT load; facility is derived (× PUE). The LARGER facility / grid figure is what generation + feasibility size against.'}
          </div>
        </div>
      ) : band ? (
        <div
          className="border border-border-subtle bg-state-hover/40 p-2 space-y-1 rounded-lg"
          data-testid="power-breakdown"
          data-figure-basis="bucket-derived"
        >
          <div className="type-meta text-text-muted font-bold">
            Power breakdown
            <span className="normal-case tracking-normal text-text-secondary ml-2">
              (PUE {formatStackNum(band.pue)} · PF {hasRecordedPowerFactor ? formatStackNum(band.powerFactor) : 'not set'})
            </span>
          </div>
          <div
            className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal"
            data-testid="power-breakdown-pue-source"
          >
            PUE source: {pueSourceLabel(band.pueSource)}
            {wasteHeatLowered && ' — lowered by BTM waste-heat recovery'}
          </div>
          <div
            className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal"
            data-testid="power-breakdown-band-qualifier"
          >
            Bucket-derived band — you selected <b>{band.bucketLabel}</b>. These are the values needed to
            serve anything <i>within</i> that band, not an exact declaration.
          </div>
          <dl className="space-y-0.5">
            <PowerStackRow
              label="IT / critical load"
              band={band.itLoadMW}
              unit="MW"
              hint={itHint}
              testId="power-breakdown-it"
            />
            <PowerStackRow
              label="Total facility"
              band={band.facilityMW}
              unit="MW"
              hint={facilityHint}
              emphasis
              testId="power-breakdown-facility"
            />
            <PowerStackRow
              label="Grid interconnection"
              band={band.gridMW}
              unit="MW"
              hint="≈ facility peak"
              testId="power-breakdown-grid"
            />
            <PowerStackRow
              label="Apparent power"
              band={hasRecordedPowerFactor ? band.apparentMVA : undefined}
              unit="MVA"
              hint="÷ PF"
              unresolvedHint="Set power factor to calculate MVA"
              testId="power-breakdown-apparent"
            />
            <PowerStackRow
              label="Generation required"
              band={band.generationRequiredMW}
              unit="MW"
              hint={`÷ (1 − ${DEFAULT_TD_LOSS_PCT}% T&D)`}
              testId="power-breakdown-generation"
            />
          </dl>
          <div
            className="text-[10px] leading-relaxed normal-case tracking-normal text-text-secondary border-t border-border-subtle pt-1.5"
            data-testid="power-breakdown-sizing-basis"
          >
            Sizing basis <b>{formatStackNum(band.sizingBasisMW)} MW</b> ({sizingBasisLabel(band.sizingBasisRule)})
            {' — '}the single figure the financial model, BoM, and firm-capacity checks already use.
            Switch to <b>Exact MW</b> above to replace it with a declared number.
          </div>
          {(band.openLow || band.openHigh) && (
            <div
              className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal"
              data-testid="power-breakdown-band-open"
            >
              This bucket is open-ended {band.openHigh ? 'above' : 'below'}; the{' '}
              {band.openHigh ? 'upper' : 'lower'} end of every layer is unbounded.
            </div>
          )}
        </div>
      ) : (
        <div
          className="text-[10px] italic text-text-muted leading-relaxed"
          data-testid="power-breakdown-empty"
        >
          Enter an exact MW or pick a range bucket above to see the IT / facility / grid / apparent (MVA) / generation breakdown.
        </div>
      )}
      </>}
    </div>
  );
};

/** One labelled row of the power breakdown.
 *
 *  EXACT mode passes `value` and renders nothing when the layer could not be
 *  resolved (null), unless the caller supplies an explanation. BUCKET mode
 *  passes `band` and renders the range plus its sizing basis — the row never collapses
 *  a band to a single number, so a bucket can't be misread as a declaration. */
const PowerStackRow: React.FC<{
  label: string;
  value?: number | null;
  band?: PowerBandLayer;
  unit: 'MW' | 'MVA';
  hint?: string;
  unresolvedHint?: string;
  emphasis?: boolean;
  testId: string;
}> = ({ label, value, band, unit, hint, unresolvedHint, emphasis, testId }) => {
  if (!band && value == null && !unresolvedHint) return null;
  return (
    <div className="flex items-baseline justify-between gap-2" data-testid={testId}>
      <dt className="type-meta text-text-muted normal-case tracking-normal">
        {label}
        {hint && <span className="text-text-muted/70 ml-1">{hint}</span>}
      </dt>
      <dd className={`text-[12px] tabular-nums ${emphasis ? 'font-bold text-text-primary' : 'text-text-secondary'}`}>
        {band ? (
          <>
            {formatPowerBandLayer(band, formatStackNum)}
            <span className="type-meta text-text-muted ml-2 normal-case tracking-normal">
              basis {formatStackNum(band.sizingBasis)}
            </span>
          </>
        ) : value == null ? (
          unresolvedHint
        ) : (
          <>
            {formatStackNum(value as number)} <span className="type-meta text-text-muted">{unit}</span>
          </>
        )}
      </dd>
    </div>
  );
};

/* ── CoolingAxesSection — BTM (site / cold-end) cooling axes ───────────────
 *
 * CHANGE 3/4 — the two BTM (behind-the-meter) cooling axes that describe how
 * heat ultimately leaves the SITE, kept distinct from the IT-side cooling loop
 * (`coolingStrategy`, rendered as pills directly above this in the clustered
 * Cooling section):
 *
 *   - `bopHeatRejection`  — cold-END heat rejection (dry / wet / absorption …)
 *   - `coolingGeneration` — BTM cold PRODUCTION machine (chiller technology)
 *
 * Both selectors honour the R-COOL-VALIDITY physical-viability gating (thermally
 * driven options enable/disable based on the phase power mix entered above) and
 * surface a soft, never-blocking stale-choice warning. This logic was relocated
 * verbatim from `PowerBasisAwarenessFields` — it depends only on `intent` +
 * `onChange`, so the move is a pure restructure, not a behaviour change.
 */
interface CoolingAxesSectionProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
}

const CoolingAxesSection: React.FC<CoolingAxesSectionProps> = ({
  intent, onChange, testIdPrefix,
}) => {
  // Cold-END SINK (where rejected heat goes). The OVERLAPPING absorption /
  // free-cooling concept is shared with the cold-production MACHINE axis below,
  // so picking the thermal `absorption-cchp` (or `free-cooling`) sink CO-WRITES
  // the consistent MACHINE via `coolingGenerationForSink` — making the
  // contradictory pair (thermal sink + electric machine) structurally impossible
  // to persist. Genuinely-distinct sinks (dry / wet / once-through / hybrid)
  // leave the MACHINE axis untouched.
  const handleBopHeatRejection = (raw: string) => {
    const sink = raw === '' ? undefined : (raw as BopHeatRejection);
    let next: ProjectIntent = { ...intent, bopHeatRejection: sink };
    const pairedGen = coolingGenerationForSink(sink);
    if (pairedGen) {
      next.coolingGeneration = pairedGen;
      next = clearIntentFieldExplicitlyUnknown(next, 'coolingGeneration');
    }
    next = sink === 'unknown'
      ? markIntentFieldExplicitlyUnknown(next, 'bopHeatRejection')
      : clearIntentFieldExplicitlyUnknown(next, 'bopHeatRejection');
    onChange(next);
  };

  // BTM cooling GENERATION (how cold is PRODUCED — absorption /
  // vapour-compression-electric / steam-driven), distinct from the cold-end SINK
  // and the IT-side `coolingStrategy`. ADDITIVE + optional. Co-writes the paired
  // SINK so the pair can never contradict: picking a thermal/free-cooling machine
  // sets the consistent sink; picking an electric / non-thermal machine clears a
  // now-contradictory `absorption-cchp` sink (→ 'unknown') via
  // `reconcileSinkForCoolingGeneration`. #157.
  const handleCoolingGeneration = (raw: string) => {
    const gen = raw === '' ? undefined : (raw as CoolingGeneration);
    let next: ProjectIntent = { ...intent, coolingGeneration: gen };
    // Picking the free-cooling machine pairs the free-cooling sink; otherwise
    // reconcile (clear a contradictory absorption-cchp sink against the machine).
    if (gen === 'free-cooling-economiser') {
      next.bopHeatRejection = 'free-cooling';
      next = clearIntentFieldExplicitlyUnknown(next, 'bopHeatRejection');
    } else {
      const reconciled = reconcileSinkForCoolingGeneration(gen, intent.bopHeatRejection);
      if (reconciled !== undefined) next.bopHeatRejection = reconciled;
    }
    next = gen === 'unknown'
      ? markIntentFieldExplicitlyUnknown(next, 'coolingGeneration')
      : clearIntentFieldExplicitlyUnknown(next, 'coolingGeneration');
    onChange(next);
  };

  // Read-only default for display when `coolingGeneration` is unset: the
  // platform's inferred cooling-generation method from the rest of the intent
  // (e.g. absorption-cchp heat-rejection ⇒ absorption-thermal). Display ONLY —
  // never written back unless the user picks a value (no silent mutation of
  // stored data). 'unknown' carries no signal, so we leave the control on
  // "Not specified" rather than pre-selecting Unknown.
  const coolingGenerationInferred = projectCoolingGeneration(intent);
  const coolingGenerationDefault: CoolingGeneration | '' =
    coolingGenerationInferred === 'unknown' ? '' : coolingGenerationInferred;

  // Physical-viability gating (R-COOL-VALIDITY) — the cooling selectors below
  // must not offer thermally-driven options (absorption / steam) for a power mix
  // with no on-site waste heat. `coolingContextFromIntent` collects the power mix
  // across every effective phase (entered ABOVE this section now); `coolingOptionsFor`
  // returns each option with a `disabled` + `disabledReason` (or a soft `warn`) so
  // the selects render uniformly. We do NOT pass a water signal here (none is
  // available on the intent), so evaporative / once-through rejection is advised,
  // never blocked.
  const coolingCtx = useMemo(() => coolingContextFromIntent(intent), [intent]);
  const heatRejectionOptions = useMemo(
    () => coolingOptionsFor('heat-rejection', coolingCtx),
    [coolingCtx],
  );
  const coolingGenerationOptions = useMemo(
    () => coolingOptionsFor('generation', coolingCtx),
    [coolingCtx],
  );

  // Soft cross-field warnings (NEVER a hard block) — if a STORED cooling choice
  // is no longer viable after a power-mix change, surface an inline note that
  // points to the viable alternative. Consistent with "never hard-block; always a
  // resolution affordance": the stored value is left untouched until the user
  // acts. Only fires when a value is actually set (an unset select carries no
  // stale choice).
  const heatRejectionStaleWarning = useMemo(() => {
    const v = intent.bopHeatRejection;
    if (!v) return null;
    const res = isHeatRejectionViable(v, coolingCtx);
    return res.viable ? null : res.reason ?? null;
  }, [intent.bopHeatRejection, coolingCtx]);

  const coolingGenerationStaleWarning = useMemo(() => {
    const v = intent.coolingGeneration;
    if (!v) return null;
    const res = isCoolingGenerationViable(v, coolingCtx);
    return res.viable ? null : res.reason ?? null;
  }, [intent.coolingGeneration, coolingCtx]);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-btm-cooling`}>
      <div className="type-meta text-text-muted font-bold">
        Site / BTM cooling
        <span className="normal-case tracking-normal text-text-secondary ml-2">
          how heat leaves the SITE
        </span>
      </div>
      <p className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal">
        Two distinct BTM (behind-the-meter) decisions: the heat-rejection method
        (where rejected heat goes) and the chiller plant (how cold is made).
        Both are distinct from the IT cooling loop above (how heat leaves the
        servers). The thermal absorption / CCHP option appears on both axes and is
        kept in sync automatically, so an impossible thermal-rejection-with-electric-chiller
        pair can't be chosen.
      </p>

      {/* BoP heat-rejection (cold-end SINK) — refines the config-derived PUE
          estimate and the BTM waste-heat credit. */}
      <label className="space-y-1 block" data-field-id="bopHeatRejection">
        <span
          className="type-meta text-text-muted block"
          title="Heat rejection: WHERE the facility's rejected heat ultimately goes (dry cooler, wet/cooling tower, once-through, hybrid). The absorption / CCHP heat-rejection path reuses on-site waste heat to drive cooling thermally — lowering electrical PUE when on-site thermal generation is present, and it sets the chiller plant below to absorption automatically."
        >
          Heat rejection
        </span>
        <select
          value={intent.bopHeatRejection ?? ''}
          onChange={(e) => handleBopHeatRejection(e.target.value)}
          aria-label="Balance-of-plant heat rejection method"
          data-testid={`${testIdPrefix}-bop-heat-rejection`}
          className="w-full max-w-xs px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal outline-none focus:ring-1 focus:ring-primary rounded-md"
        >
          <option value="">Not specified</option>
          {heatRejectionOptions.map((o) => (
            <option
              key={o.value}
              value={o.value}
              disabled={o.disabled}
              title={o.disabled ? o.disabledReason : o.warnReason}
            >
              {o.label}
              {o.disabled ? ' — unavailable' : ''}
            </option>
          ))}
        </select>
        {(intent.bopHeatRejection === 'unknown' || isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection')) && (
          <p
            className="text-[10px] leading-relaxed text-warning"
            data-testid={`${testIdPrefix}-bop-heat-rejection-unknown-recommendation`}
          >
            Unknown recorded · follow-up remains open. Recommendation: Shortlist dry, hybrid, and wet rejection against local design conditions, then obtain a vendor capacity check.
          </p>
        )}
        {heatRejectionStaleWarning && (
          <span
            className="block text-[10px] italic text-warning leading-relaxed normal-case tracking-normal"
            data-testid={`${testIdPrefix}-bop-heat-rejection-stale`}
          >
            Selected heat-rejection method is no longer viable — {heatRejectionStaleWarning}.
          </span>
        )}
        <span className="block text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal">
          WHERE rejected heat goes. The absorption / CCHP heat-rejection path IS the waste-heat machine — picking it sets the chiller plant below to absorption (thermal) automatically. It lowers electrical PUE when on-site thermal generation (gas / SMR / nuclear) is present.
        </span>
      </label>

      {/* BTM cooling GENERATION (how cooling is produced) — distinct from the
          cold-end heat-rejection method above and the IT-side coolingStrategy.
          ADDITIVE + optional. #157. Pre-fills (display only) from the inferred
          default when unset; the stored value is only written on user pick. */}
      <label className="space-y-1 block" data-field-id="coolingGeneration">
        <span
          className="type-meta text-text-muted block"
          title="Chiller plant: HOW cold is made behind the meter — thermally-driven absorption (waste-heat), electric vapour-compression chillers, or steam-driven. Distinct from the heat-rejection method (where heat goes) and the IT-side cooling strategy (how heat leaves the servers). Kept consistent with the heat-rejection method automatically."
        >
          Chiller plant
        </span>
        <select
          value={intent.coolingGeneration ?? coolingGenerationDefault}
          onChange={(e) => handleCoolingGeneration(e.target.value)}
          aria-label="Behind-the-meter cooling generation method"
          data-testid={`${testIdPrefix}-cooling-generation`}
          className="w-full max-w-xs px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal outline-none focus:ring-1 focus:ring-primary rounded-md"
        >
          <option value="">Not specified</option>
          {coolingGenerationOptions.map((o) => (
            <option
              key={o.value}
              value={o.value}
              disabled={o.disabled}
              title={o.disabled ? o.disabledReason : o.warnReason}
            >
              {o.label}
              {o.disabled ? ' — unavailable' : ''}
            </option>
          ))}
        </select>
        {coolingGenerationStaleWarning && (
          <span
            className="block text-[10px] italic text-warning leading-relaxed normal-case tracking-normal"
            data-testid={`${testIdPrefix}-cooling-generation-stale`}
          >
            Selected chiller plant is no longer viable — {coolingGenerationStaleWarning}.
          </span>
        )}
        <span className="block text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal">
          HOW cold is made behind the meter — thermally-driven absorption (waste-heat), electric vapour-compression chillers, or steam-driven. Distinct from the heat-rejection method above (where heat goes) and the IT cooling loop (how heat leaves the servers).
        </span>
      </label>
    </div>
  );
};

/* ── TimelineField — dual-mode Exact date / Range bucket input ─────────
 *
 * Project_Intent_Exact_Inputs_Followup PMT3. Mirrors PowerRequirementField
 * above one-for-one so the dual-input pattern stays consistent across
 * the form:
 *
 *   ○ Exact date  ● Range bucket
 *
 * In Exact mode the date input is editable; the range pills are
 * disabled. In Range mode the date is cleared and the pills behave
 * like the legacy form. Same dual-write discipline as MW —
 * `withInServiceDate()` and `withTimelineBucket()` keep `inServiceDate`
 * and `timeline` consistent regardless of which mode the user drives.
 *
 * Validation: date must parse AND be in the future. We also impose a
 * 20-year upper bound — anything past 2046 is almost certainly a typo
 * (project planning horizons rarely exceed 10 years).
 */
interface TimelineFieldProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
  /** When phasing is enabled, tip text reflects per-phase COD precision. */
  phases?: ProjectPhase[];
}

/* ── Phased rollout editor — PP pack PP3 ────────────────────────────────
 *
 * Toggle-driven: OFF (default) preserves the legacy single-phase form
 * exactly. ON reveals a per-phase card list with capacity / exact COD /
 * power source. (Per-phase status, cooling override, offtaker, notes and the
 * COD range mode were all removed — none had a deterministic consumer.)
 *
 * Reorder via up/down arrow buttons (no drag library installed —
 * keyboard-accessible chevrons work without adding 30KB of dnd code).
 *
 * Validation surfaced inline (non-blocking — the form's outer Save
 * button decides what to do with errors):
 *   - Phase MW must be > 0 when set
 *   - Phase CODs in sequence order (Phase 1 COD ≤ Phase 2 COD …)
 *   - Top-level MW shown read-only as "sum of phases"
 */
// Canonical order lives in types/siteRag (shared with the recommendation
// engine's deterministic tie-break) — re-exported here for local readability.
const PHASE_POWER_COMPONENT_KEYS = CANONICAL_PHASE_POWER_COMPONENT_KEYS;

const GRID_CONNECTION_KEYS: GridConnectionMode[] = ['islanded', 'grid-connected', 'undecided'];

interface PhaseCodFieldProps {
  phase: ProjectPhase;
  onChange: (next: ProjectPhase) => void;
  testIdPrefix: string;
  codOrderError?: string | null;
}

const PhaseCodField: React.FC<PhaseCodFieldProps> = ({
  phase, onChange, testIdPrefix, codOrderError,
}) => {
  // EXACT DATE ONLY. The Exact / Range mode toggle and the coarse COD bucket
  // pills were removed (intent-input audit): a bucket-only phase resolves to a
  // NULL in-service date in `effectivePhaseTimeline`, so sequencing risks,
  // lead-time risks and the pathway COD year all ignored it — the range only
  // ever reached LLM prompt text. `codTimeline` is still DERIVED from the date
  // by `withPhaseInServiceDate()`; a legacy bucket-only phase is surfaced
  // read-only below so it is never hidden state.
  const [dateInput, setDateInput] = useState<string>(phase.targetInServiceDate ?? '');

  useEffect(() => {
    setDateInput(phase.targetInServiceDate ?? '');
  }, [phase.targetInServiceDate]);

  const { minIso, maxIso } = useMemo(() => {
    const today = new Date();
    const minD = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const maxD = new Date(today);
    maxD.setFullYear(maxD.getFullYear() + MAX_PLANNING_YEARS);
    return {
      minIso: minD.toISOString().slice(0, 10),
      maxIso: maxD.toISOString().slice(0, 10),
    };
  }, []);

  const validationError = useMemo(() => {
    if (dateInput.trim() === '') return null;
    const t = new Date(dateInput);
    if (!Number.isFinite(t.getTime())) return 'Enter a valid date (YYYY-MM-DD).';
    if (t.getTime() <= Date.now()) return 'Date must be in the future.';
    const max = new Date(maxIso);
    if (t.getTime() > max.getTime()) {
      return `Date is too far in the future (max ${MAX_PLANNING_YEARS} years).`;
    }
    return null;
  }, [dateInput, maxIso]);

  const handleDateInput = (raw: string) => {
    setDateInput(raw);
    if (raw.trim() === '') {
      // Clearing the date means "no COD recorded" — never fall back to a stale
      // derived bucket.
      if (phase.targetInServiceDate) {
        onChange(withPhaseTimelineBucket(phase, 'undecided'));
      }
      return;
    }
    const t = new Date(raw);
    if (!Number.isFinite(t.getTime())) return;
    if (t.getTime() <= Date.now()) return;
    const max = new Date(maxIso);
    if (t.getTime() > max.getTime()) return;
    onChange(withPhaseInServiceDate(phase, raw));
  };

  // Legacy persisted phases may carry ONLY a coarse bucket (the removed range
  // mode). Show it read-only so the user can see it and replace it with a date.
  const legacyBucket = !phase.targetInServiceDate
    && phase.codTimeline != null
    && phase.codTimeline !== 'undecided'
    ? phase.codTimeline
    : null;

  return (
    <div className="col-span-2 space-y-1.5">
      <div className="type-meta text-text-muted">Target COD</div>
      <input
        type="date"
        min={minIso}
        max={maxIso}
        value={dateInput}
        onChange={(e) => handleDateInput(e.target.value)}
        aria-label="Exact phase COD"
        aria-invalid={codOrderError != null || validationError != null}
        data-testid={`${testIdPrefix}-cod-date`}
        className={`w-36 px-2 py-1 text-[12px] bg-canvas border rounded-md ${
          codOrderError || validationError ? 'border-danger' : 'border-border-subtle'
        } text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary`}
      />
      {legacyBucket && (
        <div
          className="text-[10px] text-text-muted normal-case tracking-normal"
          data-testid={`${testIdPrefix}-cod-legacy-range`}
        >
          Previously recorded as a range ({TIMELINE_LABELS[legacyBucket]}). Enter a date to replace it —
          only an exact COD drives sequencing, lead-time and pathway timing.
        </div>
      )}
      {validationError && (
        <div className="text-[10px] text-danger" role="alert" data-testid={`${testIdPrefix}-cod-date-error`}>
          {validationError}
        </div>
      )}
    </div>
  );
};

/* ── PhaseCapacityField — dual-mode Exact MW / Range bucket per phase ───
 *
 * The capacity sibling of PhaseCodField above, mirroring the site-level
 * PowerRequirementField trio: Exact MW, Range bucket, or (ultimate phase
 * only) the max-power study's derived figure. All writes go through the
 * projectIntent.ts capacity writers so the exact/bucket pair never drifts
 * and a manual edit always drops the derived-figure provenance. */
interface PhaseCapacityFieldProps {
  phase: ProjectPhase;
  onChange: (next: ProjectPhase) => void;
  testIdPrefix: string;
  /** Monotonic-non-decreasing violation for THIS phase (advisory). */
  capacityError?: string | null;
  /** MW ADDED this phase — parent suppresses it after a non-exact prior phase. */
  incrementMW?: number | null;
  /** True only for the max-sequence phase — the sole phase offered "Use site
   *  max": under the cumulative convention "max" is the build-out end-state. */
  isUltimate: boolean;
  /** The intent-level max-power stamp, when one exists. Presence-gated. */
  maxPowerStudy?: ProjectIntent['maxPowerStudy'] | null;
}

const PhaseCapacityField: React.FC<PhaseCapacityFieldProps> = ({
  phase, onChange, testIdPrefix, capacityError, incrementMW, isUltimate, maxPowerStudy,
}) => {
  const cap = effectivePhaseCapacity(phase);
  const initialMode: 'exact' | 'range' =
    phase.capacityMW == null && phase.capacityBucket != null && phase.capacityBucket !== 'undecided'
      ? 'range'
      : 'exact';
  const [mode, setMode] = useState<'exact' | 'range'>(initialMode);

  // External writes (Use-site-max, suggestion chips) land an exact figure —
  // follow them into exact mode. Never force range on a cleared input: a
  // mid-edit empty field must not yank the input away (unlike the COD date
  // picker, number inputs pass through empty intermediate states).
  useEffect(() => {
    if (phase.capacityMW != null) setMode('exact');
  }, [phase.capacityMW]);

  const switchToExact = () => setMode('exact');
  const switchToRange = () => {
    if (mode === 'range') return;
    setMode('range');
    // Only write when there is an exact figure (or derived provenance) to
    // clear — a pure-drafting phase stays untouched so a mode click alone
    // never dirties the intent.
    if (phase.capacityMW != null || phase.capacitySource != null) {
      onChange(withPhaseCapacityBucket(phase, phase.capacityBucket ?? 'undecided'));
    }
  };

  const handleMwInput = (raw: string) => {
    const n = raw === '' ? null : Number(raw);
    onChange(withPhaseCapacityMW(phase, Number.isFinite(n as number) ? (n as number) : null));
  };

  const handleBucketSelect = (value: string) => {
    onChange(withPhaseCapacityBucket(phase, value as PowerBucket));
  };

  const derived = phase.capacitySource === 'site-max-power-study';
  const showUseMax = isUltimate && maxPowerStudy != null
    && !(derived && phase.capacityMW === maxPowerStudy.facilityMW);
  // Legacy 'over-200mw' is never OFFERED as a new choice, but a persisted one
  // must keep resolving — surface it as a selectable option only when stored.
  const bucketOptions: PowerBucket[] = phase.capacityBucket === 'over-200mw'
    ? [...POWER_BUCKET_VALUES, 'over-200mw']
    : [...POWER_BUCKET_VALUES];

  return (
    <div className="space-y-1.5">
      <div className="type-meta text-text-muted">Total Capacity at Phase Completion</div>
      <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Phase capacity entry mode">
        <label className="inline-flex items-center gap-1 type-meta text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-mw-mode`}
            checked={mode === 'exact'}
            onChange={switchToExact}
            data-testid={`${testIdPrefix}-mw-mode-exact`}
            className="accent-text-primary"
          />
          Exact
        </label>
        <label className="inline-flex items-center gap-1 type-meta text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-mw-mode`}
            checked={mode === 'range'}
            onChange={switchToRange}
            data-testid={`${testIdPrefix}-mw-mode-range`}
            className="accent-text-primary"
          />
          Range
        </label>
      </div>
      {mode === 'exact' ? (
        <input
          type="number"
          min={0.1}
          step={0.1}
          value={phase.capacityMW ?? ''}
          onChange={(e) => handleMwInput(e.target.value)}
          data-testid={`${testIdPrefix}-mw`}
          placeholder="50"
          aria-label="Total capacity at phase completion (MW)"
          aria-invalid={capacityError != null}
          className={`w-full px-2 py-1 text-[12px] bg-canvas border text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary rounded-md ${
            capacityError ? 'border-danger' : 'border-border-subtle'
          }`}
        />
      ) : (
        <select
          value={phase.capacityBucket ?? 'undecided'}
          onChange={(e) => handleBucketSelect(e.target.value)}
          data-testid={`${testIdPrefix}-mw-bucket`}
          aria-label="Total capacity range at phase completion"
          aria-invalid={capacityError != null}
          className={`w-full px-2 py-1 text-[12px] bg-canvas border text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary rounded-md ${
            capacityError ? 'border-danger' : 'border-border-subtle'
          }`}
        >
          {bucketOptions.map(b => (
            <option key={b} value={b}>{POWER_REQUIREMENT_LABELS[b]}</option>
          ))}
        </select>
      )}
      {/* CUMULATIVE convention: this is the TOTAL site capacity once this
          phase completes; the Δ below is the new MW ADDED this phase. */}
      {mode === 'exact' && incrementMW != null && (
        <div className="text-[10px] text-text-secondary normal-case" data-testid={`${testIdPrefix}-mw-increment`}>
          +{incrementMW} MW this phase
        </div>
      )}
      {mode === 'range' && !cap.isExact && cap.sizingBasisMW != null && cap.sizingBasisRule != null && (
        <div className="text-[10px] text-text-muted normal-case" data-testid={`${testIdPrefix}-mw-sizing-basis`}>
          Sizing basis {cap.sizingBasisMW} MW ({sizingBasisLabel(cap.sizingBasisRule)}) — cumulative at completion.
          {isUltimate ? ' As the final phase, this range becomes the campus band.' : ''}
        </div>
      )}
      {derived && (
        <div className="text-[10px] text-text-secondary normal-case" data-testid={`${testIdPrefix}-mw-derived`}>
          Derived from the max-power study — editing replaces it with a declared figure.
        </div>
      )}
      {showUseMax && (
        <button
          type="button"
          onClick={() => onChange(withPhaseCapacityMax(phase, maxPowerStudy.facilityMW))}
          data-testid={`${testIdPrefix}-mw-use-max`}
          className="rounded-md border border-border-subtle px-2 py-1 type-meta font-bold text-text-secondary transition-colors hover:border-border hover:text-text-primary"
        >
          Use site max ({Math.round(maxPowerStudy.facilityMW)} MW facility)
        </button>
      )}
      {capacityError && (
        <div className="text-[10px] text-danger normal-case" role="alert" data-testid={`${testIdPrefix}-mw-error`}>
          {capacityError}
        </div>
      )}
    </div>
  );
};

/* ── PhaseSourceMwCell — per-source exact ⟷ range capacity entry ─────────
 *
 * Mirrors PhaseCapacityField's mode discipline on the per-source axis:
 *   - initial mode from the STORED fields (range only when no exact figure
 *     and at least one edge is present);
 *   - an EXTERNAL exact write (scenario promotion) flips the cell back to
 *     exact — safe because switching to range clears the exact figure
 *     synchronously, so the follow-effect can never fight the user;
 *   - all MW writes go through the paired lib writers, which enforce
 *     exact-wins (each mode's write drops the other mode's fields).
 */
const PhaseSourceMwCell: React.FC<{
  phase: ProjectPhase;
  sourceKey: PhasePowerComponentKey;
  sourceShrank: boolean;
  onPhaseChange: (next: ProjectPhase) => void;
  /** Full testid prefix for this source row, e.g. `t-phase-0-mix-grid`. */
  cellTestId: string;
}> = ({ phase, sourceKey, sourceShrank, onPhaseChange, cellTestId }) => {
  const label = PHASE_POWER_COMPONENT_LABEL[sourceKey];
  const sourceCap = effectivePhaseSourceCapacity(phase, sourceKey);
  const stored = phase.powerSourceConfig?.componentParams?.[sourceKey];
  const [mode, setMode] = useState<'exact' | 'range'>(
    !sourceCap.isExact && (stored?.capacityMinMW != null || stored?.capacityMaxMW != null)
      ? 'range'
      : 'exact',
  );
  useEffect(() => {
    // Follow an external exact write (e.g. promotion) into exact mode.
    if (sourceCap.isExact && mode !== 'exact') setMode('exact');
  }, [sourceCap.isExact, mode]);

  const unsized = sourceCap.kind === 'unsized';
  const inputClass = (flag: boolean) =>
    `w-20 px-2 py-1 text-[12px] bg-canvas border text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary rounded-md ${
      sourceShrank ? 'border-danger' : flag ? 'border-amber-500 ring-1 ring-amber-500/40' : 'border-border-subtle'
    }`;

  const switchMode = (next: 'exact' | 'range') => {
    if (mode === next) return;
    setMode(next);
    // No-dirty rule with one exception: entering the other mode clears the
    // now-inactive figure so the stored doc never carries both (and so the
    // follow-effect above can't flip the toggle back).
    if (next === 'range' && sourceCap.isExact) {
      onPhaseChange(withPhaseSourceCapacityRange(phase, sourceKey, null, null));
    } else if (next === 'exact' && (stored?.capacityMinMW != null || stored?.capacityMaxMW != null)) {
      onPhaseChange(withPhaseSourceCapacityMW(phase, sourceKey, null));
    }
  };

  const numberOrNull = (raw: string): number | null => {
    if (raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <>
      <div
        role="radiogroup"
        aria-label={`${label} capacity entry mode`}
        className="inline-flex items-center gap-1"
      >
        <PillButton
          active={mode === 'exact'}
          onClick={() => switchMode('exact')}
          testId={`${cellTestId}-mode-exact`}
        >
          Exact
        </PillButton>
        <PillButton
          active={mode === 'range'}
          onClick={() => switchMode('range')}
          testId={`${cellTestId}-mode-range`}
        >
          Range
        </PillButton>
      </div>
      {mode === 'exact' ? (
        <label className="inline-flex items-center gap-1 type-meta text-text-muted">
          <input
            type="number"
            min={0}
            step={1}
            value={stored?.capacityMW ?? ''}
            onChange={(e) =>
              onPhaseChange(withPhaseSourceCapacityMW(phase, sourceKey, numberOrNull(e.target.value)))
            }
            placeholder="0"
            aria-label={`${label} total MW at phase completion`}
            aria-invalid={sourceShrank}
            data-testid={`${cellTestId}-mw`}
            className={inputClass(unsized)}
          />
          MW
          {unsized && !sourceShrank && (
            <span className="text-[10px] text-amber-500 normal-case tracking-normal ml-1">enter MW</span>
          )}
        </label>
      ) : (
        <label className="inline-flex items-center gap-1 type-meta text-text-muted">
          <input
            type="number"
            min={0}
            step={1}
            value={stored?.capacityMinMW ?? ''}
            onChange={(e) =>
              onPhaseChange(
                withPhaseSourceCapacityRange(
                  phase, sourceKey, numberOrNull(e.target.value), stored?.capacityMaxMW ?? null,
                ),
              )
            }
            placeholder="min"
            aria-label={`${label} minimum cumulative MW at phase completion`}
            aria-invalid={sourceShrank}
            data-testid={`${cellTestId}-mw-min`}
            className={inputClass(unsized)}
          />
          –
          <input
            type="number"
            min={0}
            step={1}
            value={stored?.capacityMaxMW ?? ''}
            onChange={(e) =>
              onPhaseChange(
                withPhaseSourceCapacityRange(
                  phase, sourceKey, stored?.capacityMinMW ?? null, numberOrNull(e.target.value),
                ),
              )
            }
            placeholder="max"
            aria-label={`${label} maximum cumulative MW at phase completion`}
            aria-invalid={sourceShrank}
            data-testid={`${cellTestId}-mw-max`}
            className={inputClass(unsized)}
          />
          MW
          {unsized && !sourceShrank && (
            <span className="text-[10px] text-amber-500 normal-case tracking-normal ml-1">enter min–max</span>
          )}
        </label>
      )}
    </>
  );
};

interface PhasePowerSourceFieldProps {
  phase: ProjectPhase;
  onChange: (next: ProjectPhase) => void;
  testIdPrefix: string;
  /**
   * The FULL phase list (carry-forward convention) + this phase's index, so the
   * per-source CUMULATIVE MW can be differenced against the prior phase to show
   * the Δ added this phase and to enforce the per-source monotonic guard.
   */
  phases: ProjectPhase[];
  index: number;
}

const PhasePowerSourceField: React.FC<PhasePowerSourceFieldProps> = ({
  phase, onChange, testIdPrefix, phases, index,
}) => {
  const config: PhasePowerSourceConfig = phase.powerSourceConfig ?? {
    components: [],
    gridConnection: 'undecided',
  };

  const updateConfig = (next: PhasePowerSourceConfig) => {
    onChange(withPhasePowerSourceConfig(phase, next));
  };

  // mixMode is OPTIONAL on the stored config and back-compat: absent reads
  // as 'or' (legacy behaviour — no combined-capacity claim, no feasibility
  // check, no per-source sizing inputs).
  const mixMode: 'or' | 'and' = config.mixMode ?? 'or';

  const toggleComponent = (key: PhasePowerComponentKey) => {
    const has = config.components.includes(key);
    const components = has
      ? config.components.filter(c => c !== key)
      : [...config.components, key];
    updateConfig({ ...config, components });
  };

  const setMixMode = (next: 'or' | 'and') => {
    if (mixMode === next) return;
    updateConfig({ ...config, mixMode: next });
  };

  // Write a single per-source param (MW or CF%), preserving the other.
  const setComponentParam = (
    key: PhasePowerComponentKey,
    patch: { capacityMW?: number; capacityFactorPct?: number },
  ) => {
    const params = config.componentParams ?? {};
    const prev = params[key] ?? {};
    updateConfig({
      ...config,
      componentParams: { ...params, [key]: { ...prev, ...patch } },
    });
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-power-mix`}>
      <div className="type-meta text-text-muted">
        Power source mix
        {config.components.length > 0 && (
          <span className="normal-case tracking-normal text-text-secondary ml-2">
            ({formatPhasePowerSource({ ...phase, powerSourceConfig: config })})
          </span>
        )}
      </div>
      <p className="type-meta text-text-muted normal-case tracking-normal leading-relaxed max-w-prose">
        <span className="font-semibold text-text-secondary">Grid import</span> is a power
        source you size in MW (your contracted / expected utility draw). The{' '}
        <span className="font-semibold text-text-secondary">Grid mode</span> pills below
        set interconnection only (islanded vs grid-connected) — they don't size any power.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-meta text-text-muted">Combine as</span>
        {(['or', 'and'] as const).map(m => (
          <PillButton
            key={m}
            active={mixMode === m}
            onClick={() => setMixMode(m)}
            testId={`${testIdPrefix}-mixmode-${m}`}
          >
            {m === 'or' ? 'Either / Or' : 'Combined (And)'}
          </PillButton>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {PHASE_POWER_COMPONENT_KEYS.map(k => (
          <PillButton
            key={k}
            active={config.components.includes(k)}
            onClick={() => toggleComponent(k)}
            testId={`${testIdPrefix}-power-${k}`}
          >
            {PHASE_POWER_COMPONENT_LABEL[k]}
          </PillButton>
        ))}
      </div>
      {config.components.length > 0 && (
        <div
          className="space-y-1.5 border-l border-border-subtle pl-2"
          data-testid={`${testIdPrefix}-mix-params`}
        >
          <div className="type-meta text-text-muted normal-case tracking-normal">
            {mixMode === 'and'
              ? 'Combined — MW splits across sources (sum ≈ phase capacity); CF = how hard each runs.'
              : 'Either / Or — each alternative is sized independently (typically the full phase capacity); CF = how hard each would run.'}
          </div>
          {config.components.map(k => {
            const p = config.componentParams?.[k];
            const cfDefault = DEFAULT_CAPACITY_FACTOR_PCT[k];
            // Per-source capacity duality — resolve BOTH phases' views once so
            // the guard, chip and sizing-basis line all share one read.
            const curCap = effectivePhaseSourceCapacity(phase, k);
            const prevPhase = index > 0 ? phases[index - 1] : undefined;
            const prevCap = prevPhase ? effectivePhaseSourceCapacity(prevPhase, k) : null;
            // CARRY-FORWARD: this source's MW is the CUMULATIVE capacity at this
            // phase's completion; the Δ below is the new MW ADDED this phase
            // (cumulative_i − cumulative_{i-1}, floor 0). CF is a rate — never
            // differenced. The increment SSOT mirrors the phase-level chip.
            // Suppressed after a RANGE-mode prior phase: no honest Δ exists
            // against a band (the increment SSOT would treat the non-finite
            // prior as 0 and book the full cumulative as "added this phase").
            const sourceIncrementMW =
              prevCap?.kind === 'range' ? null : perSourceIncrementMW(phases, k, index);
            // Per-source monotonic guard — a source's CUMULATIVE capacity can't
            // shrink phase-over-phase. Band-aware: flag ONLY a definite shrink
            // (this phase's upper bound entirely below the prior lower bound);
            // overlapping ranges are consistent, not violations.
            const curUpper =
              curCap.kind === 'exact' ? curCap.mw
                : curCap.kind === 'range' ? curCap.maxMW : null;
            const prevLower =
              prevCap == null ? null
                : prevCap.kind === 'exact' ? prevCap.mw
                  : prevCap.kind === 'range' ? prevCap.minMW : null;
            const sourceShrank =
              curUpper != null && prevLower != null && curUpper < prevLower;
            return (
              <div key={k} className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-mix-${k}-row`}>
                <span className="type-meta text-text-secondary w-28 normal-case tracking-normal">
                  {PHASE_POWER_COMPONENT_LABEL[k]}
                </span>
                <PhaseSourceMwCell
                  phase={phase}
                  sourceKey={k}
                  sourceShrank={sourceShrank}
                  onPhaseChange={onChange}
                  cellTestId={`${testIdPrefix}-mix-${k}`}
                />
                <label className="inline-flex items-center gap-1 type-meta text-text-muted">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={p?.capacityFactorPct ?? cfDefault}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === '' ? undefined : Number(raw);
                      setComponentParam(k, {
                        capacityFactorPct: Number.isFinite(n as number) ? (n as number) : undefined,
                      });
                    }}
                    aria-label={`${PHASE_POWER_COMPONENT_LABEL[k]} capacity factor percent`}
                    data-testid={`${testIdPrefix}-mix-${k}-cf`}
                    className="w-16 px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary rounded-md"
                  />
                  % CF
                </label>
                {/* CARRY-FORWARD: the MW above is CUMULATIVE-at-completion; this
                    Δ is the new MW ADDED this phase per source (mirrors the
                    phase-level "+N MW this phase" chip). Calcs consume this
                    increment (the additional capacity), not the cumulative. */}
                {sourceIncrementMW != null && (
                  <span
                    className="text-[10px] text-text-secondary normal-case tracking-normal w-full pl-28"
                    data-testid={`${testIdPrefix}-mix-${k}-increment`}
                  >
                    +{sourceIncrementMW} MW added this phase
                  </span>
                )}
                {curCap.kind === 'range' && curCap.sizingBasisMW != null && (
                  <span
                    className="text-[10px] text-text-secondary normal-case tracking-normal w-full pl-28"
                    data-testid={`${testIdPrefix}-mix-${k}-sizing-basis`}
                  >
                    Sizing basis {Math.round(curCap.sizingBasisMW * 10) / 10} MW (midpoint of {curCap.minMW}–{curCap.maxMW} MW) — cumulative at completion
                  </span>
                )}
                {sourceShrank && (
                  <span
                    className="text-[10px] text-danger normal-case tracking-normal w-full pl-28"
                    role="alert"
                    data-testid={`${testIdPrefix}-mix-${k}-error`}
                  >
                    {prevCap?.kind === 'exact'
                      ? <>{PHASE_POWER_COMPONENT_LABEL[k]} cumulative MW can&apos;t shrink — must be ≥ Phase {prevPhase?.sequence} ({prevLower} MW).</>
                      : <>{PHASE_POWER_COMPONENT_LABEL[k]} sits entirely below Phase {prevPhase?.sequence} ({prevCap?.displayLabel}) — cumulative capacity can&apos;t shrink.</>}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-meta text-text-muted">Grid mode</span>
        {GRID_CONNECTION_KEYS.map(k => (
          <PillButton
            key={k}
            active={config.gridConnection === k}
            onClick={() => updateConfig({ ...config, gridConnection: k })}
            testId={`${testIdPrefix}-grid-${k}`}
          >
            {GRID_CONNECTION_LABEL[k]}
          </PillButton>
        ))}
        {config.gridConnection === 'grid-connected' && (
          <input
            type="text"
            value={config.gridUtilityName ?? ''}
            onChange={(e) => updateConfig({
              ...config,
              gridUtilityName: e.target.value || undefined,
            })}
            placeholder="Utility name (e.g. Entergy)"
            data-testid={`${testIdPrefix}-grid-utility`}
            aria-label="Grid utility name"
            className="px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary normal-case tracking-normal focus:outline-none focus:ring-1 focus:ring-primary rounded-md"
          />
        )}
      </div>
    </div>
  );
};

/* ── PhaseRegulatoryFields — regulatory screening inputs per phase ──────
 *
 * Two optional answers for the regulatory feasibility screen:
 *   - export mode, shown only for a grid-connected phase (an islanded or
 *     undecided phase has no grid export to describe);
 *   - operating duty of the phase's on-site generation, shown once the phase
 *     has a non-grid source (or a duty is already recorded).
 * Neither is required. "Unknown" records the explicit-unknown marker at
 * `phases.<id>.powerSourceConfig.exportMode` / `phases.<id>.generationDuty`;
 * nothing is written until the user touches a control.
 */
type PhaseUnknownMarker = { path: string; unknown: boolean };

const EXPORT_MODE_OPTIONS: ReadonlyArray<{ value: 'non-export' | 'export'; label: string }> = [
  { value: 'non-export', label: 'Grid-parallel, no export' },
  { value: 'export', label: 'Grid-parallel, may export' },
];
const GENERATION_DUTY_OPTIONS: ReadonlyArray<{ value: Exclude<NonNullable<ProjectPhase['generationDuty']>, 'unknown'>; label: string }> = [
  { value: 'emergency-standby', label: 'Emergency / standby only' },
  { value: 'peaking', label: 'Peaking' },
  { value: 'prime-continuous', label: 'Prime / continuous' },
  { value: 'mixed', label: 'Mixed duties' },
];

const PhaseRegulatoryFields: React.FC<{
  phase: ProjectPhase;
  testIdPrefix: string;
  isFieldUnknown: (path: string) => boolean;
  onMarkedChange: (next: ProjectPhase, marker: PhaseUnknownMarker) => void;
}> = ({ phase, testIdPrefix, isFieldUnknown, onMarkedChange }) => {
  const config = phase.powerSourceConfig;
  const exportPath = `phases.${phase.id}.powerSourceConfig.exportMode`;
  const dutyPath = `phases.${phase.id}.generationDuty`;
  const showExport = config?.gridConnection === 'grid-connected';
  const showDuty = (config?.components ?? []).some(component => component !== 'grid')
    || phase.generationDuty != null || isFieldUnknown(dutyPath);
  const exportValue = isFieldUnknown(exportPath) || config?.exportMode === 'unknown'
    ? EXPLICIT_UNKNOWN_SELECT_VALUE : (config?.exportMode ?? '');
  const dutyValue = isFieldUnknown(dutyPath) || phase.generationDuty === 'unknown'
    ? EXPLICIT_UNKNOWN_SELECT_VALUE : (phase.generationDuty ?? '');
  const selectClass = 'w-full h-8 px-2 border border-border bg-surface text-text-primary text-[12px] rounded-md';

  const setExportMode = (raw: string) => {
    if (!config) return;
    const { exportMode: _previous, ...rest } = config;
    void _previous;
    const exportMode = raw === EXPLICIT_UNKNOWN_SELECT_VALUE ? 'unknown' as const
      : raw === 'non-export' || raw === 'export' ? raw : undefined;
    onMarkedChange(withPhasePowerSourceConfig(phase, exportMode ? { ...rest, exportMode } : rest),
      { path: exportPath, unknown: raw === EXPLICIT_UNKNOWN_SELECT_VALUE });
  };
  const setDuty = (raw: string) => {
    const { generationDuty: _previous, ...rest } = phase;
    void _previous;
    const duty = raw === EXPLICIT_UNKNOWN_SELECT_VALUE ? 'unknown' as const
      : GENERATION_DUTY_OPTIONS.find(option => option.value === raw)?.value;
    onMarkedChange(duty ? { ...rest, generationDuty: duty } : rest, { path: dutyPath, unknown: raw === EXPLICIT_UNKNOWN_SELECT_VALUE });
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-regulatory`}>
      <p className="type-meta text-text-muted normal-case tracking-normal leading-relaxed max-w-prose" data-testid={`${testIdPrefix}-regulatory-help`}>
        Behind-the-meter generation can run islanded or grid-parallel. Zero export still needs the
        utility&apos;s interconnection review, and emergency approval does not permit prime operation.
      </p>
      {(showExport || showDuty) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {showExport && (
            <label className="space-y-1 type-meta text-text-muted" data-field-id="exportMode">
              <span>Grid export (optional)</span>
              <select
                value={exportValue}
                onChange={(e) => setExportMode(e.target.value)}
                aria-label={`Phase ${phase.sequence} grid export mode`}
                data-testid={`${testIdPrefix}-export-mode`}
                className={selectClass}
              >
                <option value="">(not set)</option>
                <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
                {EXPORT_MODE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          )}
          {showDuty && (
            <label className="space-y-1 type-meta text-text-muted" data-field-id="generationDuty">
              <span>On-site generation duty (optional)</span>
              <select
                value={dutyValue}
                onChange={(e) => setDuty(e.target.value)}
                aria-label={`Phase ${phase.sequence} on-site generation operating duty`}
                data-testid={`${testIdPrefix}-generation-duty`}
                className={selectClass}
              >
                <option value="">(not set)</option>
                <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
                {GENERATION_DUTY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

/* ── PhaseFeasibilityVerdict — R-FIN-PHASE-FEASIBILITY badge ───────────
 *
 * Renders the phase power mix adequacy verdict beneath the power-source
 * editor. In AND mode, it checks if the combined mix covers the load + reserve.
 * In OR mode, it checks each alternative independently and aggregates the verdict.
 * Verdict → token-class mapping:
 *   - feasible   → primary (firm capacity + average energy covers the load)
 *   - marginal   → amber/warning (energy-adequate but firm-short, or mixed OR)
 *   - infeasible → rose/danger (energy-short or completely firm-short)
 */
const PhaseFeasibilityVerdict: React.FC<{
  phase: ProjectPhase;
  /** Intent-level firm-capacity context — threads the same denominator every
   *  report surface grades with, so the in-form badge cannot disagree with
   *  the report verdict on the same phase. */
  siteContext?: PhaseFeasibilitySiteContext;
}> = ({ phase, siteContext }) => {
  const feasibility = useMemo(
    () => assessPhaseFeasibility(phase, siteContext),
    [phase, siteContext],
  );
  if (!feasibility) return null;

  const tone =
    feasibility.verdict === 'feasible'
      ? 'border-primary text-primary bg-primary/10'
      : feasibility.verdict === 'marginal'
        ? 'border-amber-500/40 text-amber-500 bg-amber-500/10'
        : 'border-rose-500/40 text-rose-500 bg-rose-500/10';

  return (
    <div
      data-testid="phase-feasibility-verdict"
      data-verdict={feasibility.verdict}
      className={`text-[10px] normal-case tracking-normal border px-2 py-1 ${tone}`}
    >
      {feasibility.message}
      {feasibility.loadBasis !== 'declared-exact' && (
        <div
          data-testid="phase-feasibility-load-basis"
          className="mt-0.5 text-text-muted"
        >
          Load basis: {feasibility.loadBasisLabel}
        </div>
      )}
    </div>
  );
};

interface PhaseCardProps {
  phase: ProjectPhase;
  index: number;
  total: number;
  /**
   * The FULL phase list — threaded down to PhasePowerSourceField so it can
   * difference each source's CUMULATIVE MW against the prior phase (per-source Δ
   * + monotonic guard) under the carry-forward convention.
   */
  phases: ProjectPhase[];
  testIdPrefix: string;
  onChange: (next: ProjectPhase) => void;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
  codOrderError?: string | null;
  /** Monotonic-non-decreasing violation for THIS phase's cumulative capacity. */
  capacityError?: string | null;
  /**
   * MW ADDED this phase (Δ increment) under the cumulative convention — the
   * value entered minus the previous phase's cumulative. Null when this
   * phase's capacity is null. Phase 0's increment == its own value.
   */
  incrementMW?: number | null;
  /** The intent-level max-power stamp — gates "Use site max" on the ultimate phase. */
  maxPowerStudy?: ProjectIntent['maxPowerStudy'] | null;
  /** Intent-level firm-capacity context for the feasibility badge. */
  feasibilityContext?: PhaseFeasibilitySiteContext;
  /** This phase's power-plan entry (host-computed; absent → block hidden). */
  planEntry?: PhasePlanEntry | null;
  planStaleReason?: string | null;
  planApplied?: boolean;
  onApplyPlan?: (phaseId: string) => void;
  /** Explicit-unknown lookup over the intent's `fieldSource` (dotted paths). */
  isFieldUnknown: (path: string) => boolean;
  /** Phase edit that also records or clears one explicit-unknown marker. */
  onMarkedChange: (next: ProjectPhase, marker: PhaseUnknownMarker) => void;
}

const PhaseCard: React.FC<PhaseCardProps> = ({
  phase, index, total, phases, testIdPrefix, onChange, onRemove, onMove, codOrderError,
  capacityError, incrementMW, maxPowerStudy, feasibilityContext,
  planEntry, planStaleReason, planApplied, onApplyPlan, isFieldUnknown, onMarkedChange,
}) => {
  const idBase = `${testIdPrefix}-phase-${index}`;
  return (
    <div
      data-testid={idBase}
      className="border border-border-subtle p-3 space-y-2 bg-canvas rounded-lg"
    >
      <div className="flex items-center gap-2">
        <span className="type-meta text-text-muted font-bold">
          Phase {phase.sequence}
        </span>
        <input
          type="text"
          value={phase.label}
          placeholder={`Phase ${phase.sequence} label`}
          onChange={(e) => onChange({ ...phase, label: e.target.value })}
          aria-label="Phase label"
          data-testid={`${idBase}-label`}
          className="flex-1 px-2 py-1 text-[12px] bg-canvas border border-border-subtle text-text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded-md"
        />
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          aria-label="Move phase up"
          data-testid={`${idBase}-up`}
          className="px-2 py-1 text-[11px] border border-border-subtle text-text-secondary hover:text-text-primary disabled:opacity-40 rounded-md"
        >↑</button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          aria-label="Move phase down"
          data-testid={`${idBase}-down`}
          className="px-2 py-1 text-[11px] border border-border-subtle text-text-secondary hover:text-text-primary disabled:opacity-40 rounded-md"
        >↓</button>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove phase"
          data-testid={`${idBase}-remove`}
          className="px-2 py-1 text-[11px] border border-border-subtle text-text-secondary hover:text-danger rounded-md"
        >✕</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <PhaseCapacityField
          phase={phase}
          onChange={onChange}
          testIdPrefix={idBase}
          capacityError={capacityError}
          incrementMW={incrementMW}
          isUltimate={index === total - 1}
          maxPowerStudy={maxPowerStudy}
        />

        <PhaseCodField
          phase={phase}
          onChange={onChange}
          testIdPrefix={idBase}
          codOrderError={codOrderError}
        />
      </div>

      <PhasePowerSourceField
        phase={phase}
        onChange={onChange}
        testIdPrefix={idBase}
        phases={phases}
        index={index}
      />
      <PhaseRegulatoryFields
        phase={phase}
        testIdPrefix={idBase}
        isFieldUnknown={isFieldUnknown}
        onMarkedChange={onMarkedChange}
      />
      <PhasePowerRecommendationBlock
        entry={planEntry}
        staleReason={planStaleReason}
        applied={planApplied}
        onApply={onApplyPlan}
        testIdPrefix={idBase}
      />

      <PhaseFeasibilityVerdict phase={phase} siteContext={feasibilityContext} />

      {/* Per-phase STATUS picker and per-phase COOLING (override) were removed:
          status is no longer modelled in the form (every entered phase is
          ACTIVE) and the IT-side cooling loop is a SITE-LEVEL decision only
          (see the clustered Cooling section below the phases editor).
          The per-phase OFFTAKER and NOTES free-text inputs were removed too
          (intent-input audit): both were prompt-only pass-throughs with no
          deterministic report / SED consumer. */}

      {codOrderError && (
        <div className="text-[10px] text-danger" role="alert" data-testid={`${idBase}-cod-error`}>
          {codOrderError}
        </div>
      )}
    </div>
  );
};

interface PhasedRolloutEditorProps {
  intent: ProjectIntent;
  onChange: (next: ProjectIntent) => void;
  testIdPrefix: string;
  /** Per-phase power recommendation (host-computed — the form has no SED
   *  access). Absent → the block renders nothing (wizard/rerun mounts). */
  phasePowerPlan?: PhasePowerPlanResolution | null;
  /** Staleness of an APPLIED plan (host-computed). */
  phasePowerPlanStaleReason?: string | null;
  /** Apply handler — routes through the host's existing intent-draft flow. */
  onApplyPhasePowerPlan?: (phaseId: string) => void;
}

const TimelineField: React.FC<TimelineFieldProps> = ({ intent, onChange, testIdPrefix, phases }) => {
  // Source of truth on first render: an `inServiceDate` that parses to
  // a future date means the user picked exact mode. Stale / past dates
  // — or unparseable strings — fall back to range mode so the user
  // doesn't get stuck staring at a broken value.
  const initialMode: 'exact' | 'range' = useMemo(() => {
    if (!intent.inServiceDate) return 'range';
    const t = new Date(intent.inServiceDate);
    if (!Number.isFinite(t.getTime())) return 'range';
    if (t.getTime() <= Date.now()) return 'range';
    return 'exact';
  }, [intent.inServiceDate]);
  const [mode, setMode] = useState<'exact' | 'range'>(initialMode);
  // Mirror `intent.inServiceDate` as a string so users can type
  // intermediate states without us round-tripping through Date() on
  // every keystroke.
  const [dateInput, setDateInput] = useState<string>(intent.inServiceDate ?? '');

  // Re-sync when the parent passes a different intent (Cancel / site
  // switch). Cheap because identity comparison short-circuits the
  // common case.
  useEffect(() => {
    if (intent.inServiceDate) {
      setDateInput(intent.inServiceDate);
      const t = new Date(intent.inServiceDate);
      if (Number.isFinite(t.getTime()) && t.getTime() > Date.now()) {
        setMode('exact');
      }
    } else if (mode === 'exact') {
      // Parent cleared the date from under us — fall back to range.
      setMode('range');
      setDateInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.inServiceDate]);

  // Pre-compute date bounds so the native picker enforces them.
  const { minIso, maxIso } = useMemo(() => {
    const today = new Date();
    const minD = new Date(today.getTime() + 24 * 60 * 60 * 1000); // tomorrow
    const maxD = new Date(today);
    maxD.setFullYear(maxD.getFullYear() + MAX_PLANNING_YEARS);
    return {
      minIso: minD.toISOString().slice(0, 10),
      maxIso: maxD.toISOString().slice(0, 10),
    };
  }, []);

  const validationError = useMemo(() => {
    if (mode !== 'exact') return null;
    if (dateInput.trim() === '') return null;
    const t = new Date(dateInput);
    if (!Number.isFinite(t.getTime())) return 'Enter a valid date (YYYY-MM-DD).';
    if (t.getTime() <= Date.now()) return 'Date must be in the future.';
    const max = new Date(maxIso);
    if (t.getTime() > max.getTime()) {
      return `Date is too far in the future (max ${MAX_PLANNING_YEARS} years).`;
    }
    return null;
  }, [mode, dateInput, maxIso]);

  const switchToExact = () => {
    if (mode === 'exact') return;
    setMode('exact');
    // Don't write to the intent yet — wait until the user picks a
    // valid date. The toggle itself shouldn't dirty the form.
  };

  const switchToRange = () => {
    if (mode === 'range') return;
    setMode('range');
    setDateInput('');
    if (intent.inServiceDate) {
      // Clear inServiceDate so effectiveTimeline() drops back to
      // bucket-only on the next read. Keep the current bucket so the
      // user doesn't lose their categorical choice.
      onChange(withTimelineBucket(intent, intent.timeline));
    }
  };

  const handleDateInput = (raw: string) => {
    setDateInput(raw);
    if (raw.trim() === '') {
      if (intent.inServiceDate) {
        // User wiped the field — drop the date but keep last-known bucket.
        onChange(withTimelineBucket(intent, intent.timeline));
      }
      return;
    }
    const t = new Date(raw);
    if (!Number.isFinite(t.getTime())) return;
    if (t.getTime() <= Date.now()) return;
    const max = new Date(maxIso);
    if (t.getTime() > max.getTime()) return;
    onChange(clearIntentFieldExplicitlyUnknown(
      withInServiceDate(intent, raw),
      'timeline',
    ));
  };

  const handleBucketClick = (b: TimelineBucket) => {
    if (mode !== 'range') return;
    const next = withTimelineBucket(intent, b);
    onChange(b === 'undecided'
      ? markIntentFieldExplicitlyUnknown(next, 'timeline')
      : clearIntentFieldExplicitlyUnknown(next, 'timeline'));
  };

  const tipText = useMemo(
    () => phaseTimelineTipText((phases?.length ?? 0) > 0 ? phases : undefined),
    [phases],
  );

  return (
    <div className="space-y-1.5">
      <div className="type-meta text-text-muted">Timeline</div>
      <div className="flex flex-wrap items-center gap-3" role="radiogroup" aria-label="Timeline entry mode">
        <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-timeline-mode`}
            checked={mode === 'exact'}
            onChange={switchToExact}
            data-testid={`${testIdPrefix}-timeline-mode-exact`}
            className="accent-text-primary"
          />
          Exact date
        </label>
        <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-text-secondary cursor-pointer">
          <input
            type="radio"
            name={`${testIdPrefix}-timeline-mode`}
            checked={mode === 'range'}
            onChange={switchToRange}
            data-testid={`${testIdPrefix}-timeline-mode-range`}
            className="accent-text-primary"
          />
          Range bucket
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-stretch border border-border-subtle rounded-md overflow-hidden">
          <input
            type="date"
            min={minIso}
            max={maxIso}
            placeholder="e.g. 2028-04-01"
            value={mode === 'exact' ? dateInput : ''}
            disabled={mode !== 'exact'}
            onChange={(e) => handleDateInput(e.target.value)}
            aria-label="Exact in-service date"
            aria-invalid={validationError != null}
            data-testid={`${testIdPrefix}-timeline-date-input`}
            title={mode === 'exact' && dateInput ? dateInput : undefined}
            className={`w-40 px-2 py-1.5 text-[12px] bg-canvas text-text-primary outline-none focus:ring-1 focus:ring-primary disabled:bg-state-hover disabled:text-text-muted ${
              validationError ? 'ring-1 ring-danger' : ''
            }`}
          />
        </div>

        <div className="flex flex-wrap gap-2" aria-disabled={mode !== 'range'}>
          {(['under-2yr', '2-5yr', 'over-5yr', 'undecided'] as const).map(t => (
            <PillButton
              key={t}
              active={mode === 'range' && intent.timeline === t}
              onClick={() => handleBucketClick(t)}
              testId={`${testIdPrefix}-timeline-${t}`}
              disabled={mode !== 'range'}
            >
              {TIMELINE_LABELS[t]}
            </PillButton>
          ))}
        </div>
      </div>

      {validationError && (
        <div
          className="text-[10px] text-danger"
          role="alert"
          data-testid={`${testIdPrefix}-timeline-date-error`}
        >
          {validationError}
        </div>
      )}

      <div className="text-[10px] italic text-text-muted leading-relaxed" data-testid={`${testIdPrefix}-timeline-tip`}>
        {tipText}
      </div>
      {(phases?.length ?? 0) > 0 && (
        <div className="text-[10px] text-text-muted">
          Per-phase CODs above take priority when phasing is enabled.
        </div>
      )}
    </div>
  );
};

const PhasedRolloutEditor: React.FC<PhasedRolloutEditorProps> = ({
  intent, onChange, testIdPrefix, phasePowerPlan, phasePowerPlanStaleReason, onApplyPhasePowerPlan,
}) => {
  const phases = intent.phases ?? [];
  const enabled = phases.length > 0;
  const planEntryFor = (phaseId: string): PhasePlanEntry | null =>
    phasePowerPlan?.status === 'available'
      ? phasePowerPlan.perPhase.find(e => e.phaseId === phaseId) ?? null
      : null;
  const planAppliedFor = (phaseId: string): boolean =>
    (intent.phasePowerPlan?.appliedPhases ?? []).some(p => p.phaseId === phaseId);
  // Same firm-capacity denominator every report surface grades with — the
  // in-form badge must never disagree with the report verdict on a phase.
  const feasibilityContext = useMemo(
    () => firmCapacitySiteContextFromIntent(intent),
    [intent],
  );

  const setPhases = (next: ProjectPhase[]) => {
    // Renormalise sequence numbers to 1..N so the user-facing labels
    // ("Phase 3") always match the position.
    const renumbered = next.map((p, i) => ({ ...p, sequence: i + 1 }));
    onChange({ ...intent, phases: renumbered });
  };

  const toggleEnabled = () => {
    if (enabled) {
      // User is turning phasing OFF — drop the phases array. Keep
      // top-level MW + COD as the user last set them; we don't auto-
      // sync from phases on disable because the user may want to enter
      // a clean ultimate number.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { phases: _drop, ...rest } = intent;
      onChange(rest);
    } else {
      onChange({ ...intent, phases: [makeEmptyPhase(1)] });
    }
  };

  const addPhase = () => {
    // CARRY-FORWARD: a new phase pre-populates ALL existing infrastructure from
    // the PRIOR phase (its power-source components + cumulative MW/CF + grid
    // mode), so the user doesn't re-pick grid/recip/etc. every phase. Stored
    // componentParams MW is CUMULATIVE-at-phase, so copying it forward verbatim
    // starts the new phase at the prior cumulative per source (Δ = 0 until the
    // user edits up). Phase 1 stays a clean slate (no prior phase).
    const prev = phases[phases.length - 1];
    const base = makeEmptyPhase(phases.length + 1);
    const cfg = prev ? carryForwardPowerConfig(prev) : undefined;
    setPhases([...phases, cfg ? withPhasePowerSourceConfig(base, cfg) : base]);
  };

  const updatePhase = (idx: number, next: ProjectPhase) => {
    const copy = phases.slice();
    copy[idx] = next;
    setPhases(copy);
  };

  // Same write as updatePhase, plus one explicit-unknown marker on the intent.
  const updatePhaseMarked = (idx: number, next: ProjectPhase, marker: PhaseUnknownMarker) => {
    const copy = phases.slice();
    copy[idx] = next;
    const renumbered: ProjectIntent = { ...intent, phases: copy.map((p, i) => ({ ...p, sequence: i + 1 })) };
    onChange(marker.unknown
      ? markIntentFieldExplicitlyUnknown(renumbered, marker.path)
      : clearIntentFieldExplicitlyUnknown(renumbered, marker.path));
  };
  const isFieldUnknown = (path: string) => isIntentFieldExplicitlyUnknown(intent, path);

  const removePhase = (idx: number) => {
    setPhases(phases.filter((_, i) => i !== idx));
  };

  const movePhase = (idx: number, delta: -1 | 1) => {
    const target = idx + delta;
    if (target < 0 || target >= phases.length) return;
    const copy = phases.slice();
    const [moved] = copy.splice(idx, 1);
    copy.splice(target, 0, moved);
    setPhases(copy);
  };

  // CoD-order validation — each phase's COD must be ≥ the previous
  // phase's COD when both are set. Surface inline on the LATER phase
  // so the error attaches to the offending date input.
  const codErrors: (string | null)[] = useMemo(() => {
    const errs: (string | null)[] = phases.map(() => null);
    for (let i = 1; i < phases.length; i += 1) {
      const prevT = effectivePhaseTimeline(phases[i - 1]);
      const curT = effectivePhaseTimeline(phases[i]);
      const prev = prevT.isExact ? prevT.inServiceDate : null;
      const cur = curT.isExact ? curT.inServiceDate : null;
      if (prev && cur && cur < prev) {
        errs[i] = `Phase ${phases[i].sequence} COD (${cur}) is before Phase ${phases[i - 1].sequence} COD (${prev}).`;
      }
    }
    return errs;
  }, [phases]);

  // Monotonic-non-decreasing capacity validation — under the CUMULATIVE
  // convention each phase's capacity is the TOTAL site capacity at that
  // phase's completion, so it can never SHRINK from one phase to the next.
  // Surface the error on the LATER (offending) phase, mirroring codErrors.
  // BAND-AWARE + advisory: with range-mode phases only a DEFINITE shrink is
  // flagged — the later phase's best case (range max) entirely below the
  // earlier phase's worst case (range min). Overlapping ranges pass; an
  // open-high later range can never definitely shrink. Exact-vs-exact keeps
  // the original message byte-identical.
  const capacityErrors: (string | null)[] = useMemo(() => {
    const errs: (string | null)[] = phases.map(() => null);
    for (let i = 1; i < phases.length; i += 1) {
      const prev = effectivePhaseCapacity(phases[i - 1]);
      const cur = effectivePhaseCapacity(phases[i]);
      if (prev.isExact && cur.isExact && prev.mw != null && cur.mw != null) {
        if (cur.mw < prev.mw) {
          errs[i] = `Cumulative capacity can't shrink — Phase ${phases[i].sequence} must be ≥ Phase ${phases[i - 1].sequence} (${prev.mw} MW).`;
        }
      } else if (
        prev.range != null && cur.range != null
        && Number.isFinite(cur.range.max) && cur.range.max < prev.range.min
      ) {
        errs[i] = `Cumulative capacity can't shrink — Phase ${phases[i].sequence} (${cur.displayLabel}) sits entirely below Phase ${phases[i - 1].sequence} (${prev.displayLabel}).`;
      }
    }
    return errs;
  }, [phases]);

  const view = useMemo(() => effectivePhases(intent), [intent]);
  const phasingTip = useMemo(() => phaseTimelineTipText(phases), [phases]);
  const firstCodLabel = view.initialInServiceDate
    ?? (phases[0] ? effectivePhaseTimeline(phases[0]).displayLabel : 'TBD');

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-phasing`}>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={toggleEnabled}
          data-testid={`${testIdPrefix}-phasing-toggle`}
          className="accent-text-primary"
        />
        <span className="text-[11px] uppercase tracking-widest text-text-secondary">
          Phased rollout — campus built in stages over time
        </span>
      </label>

      {enabled && (
        <div className="space-y-2" data-testid={`${testIdPrefix}-phasing-editor`}>
          <div className="space-y-2">
            {phases.map((p, i) => (
              <PhaseCard
                key={p.id}
                phase={p}
                index={i}
                total={phases.length}
                phases={phases}
                feasibilityContext={feasibilityContext}
                planEntry={planEntryFor(p.id)}
                planStaleReason={phasePowerPlanStaleReason}
                planApplied={planAppliedFor(p.id)}
                onApplyPlan={onApplyPhasePowerPlan}
                testIdPrefix={testIdPrefix}
                onChange={(next) => updatePhase(i, next)}
                isFieldUnknown={isFieldUnknown}
                onMarkedChange={(next, marker) => updatePhaseMarked(i, next, marker)}
                onRemove={() => removePhase(i)}
                onMove={(delta) => movePhase(i, delta)}
                codOrderError={codErrors[i]}
                capacityError={capacityErrors[i]}
                // Suppress the Δ chip after a NON-EXACT prior phase:
                // `phaseIncrementMW` treats a null prior cumulative as 0
                // (load-bearing for finance — do not change it), which would
                // render a phase after a RANGE phase as "+full cumulative MW
                // this phase". No honest increment exists against a range.
                incrementMW={
                  i === 0 || (phases[i - 1].capacityMW != null && Number.isFinite(phases[i - 1].capacityMW as number))
                    ? phaseIncrementMW(phases, i)
                    : null
                }
                maxPowerStudy={intent.maxPowerStudy ?? null}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addPhase}
            data-testid={`${testIdPrefix}-phasing-add`}
            className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold border border-border-subtle text-text-secondary hover:text-text-primary hover:border-border rounded-md"
          >
            + Add phase
          </button>

          <div
            className="text-[10px] italic text-text-muted leading-relaxed"
            data-testid={`${testIdPrefix}-phasing-tip`}
          >
            {phasingTip}
          </div>

          <div
            className="text-[10px] italic text-text-muted leading-relaxed"
            data-testid={`${testIdPrefix}-phasing-capacity-tip`}
          >
            Tip: enter the TOTAL site capacity at each phase&apos;s completion (cumulative,
            not the amount added). The &ldquo;+N MW this phase&rdquo; delta shows the new
            capacity added that phase, and &ldquo;Total ultimate capacity&rdquo; below is the
            final phase&apos;s figure. Example: a 50 / 100 / 200 / 300 MW build has an
            ultimate capacity of 300 MW (adding 50, 50, 100, 100 each phase).
          </div>

          <div
            className="type-meta text-text-muted"
            data-testid={`${testIdPrefix}-phasing-summary`}
          >
            Total ultimate capacity: {view.totalCapacityMW != null
              ? `${view.totalCapacityMW} MW`
              : view.ultimateCapacityBucket != null
                ? `${POWER_BUCKET_LABEL[view.ultimateCapacityBucket]} (range)`
                : 'TBD'}
            {' · '}First COD: {firstCodLabel}
            {' · '}Tech mix: {view.techMixSummary}
          </div>
        </div>
      )}
    </div>
  );
};

/** NSW1 — render mode controls density + chrome but NOT field membership.
 *   - 'overview' — full SiteConfigurationPanel treatment (banded, AI preview,
 *                  optional band expanded by default).
 *   - 'wizard'   — same band layout BUT optional band collapses to a
 *                  `<details>` "+ N more options" expander so Step 3 stays
 *                  fast without dumbing the form down. AI preview hidden.
 *   - 'rerun'    — same as 'overview' minus the AI preview (RR5 territory).
 */
export type ProjectIntentFormMode = 'overview' | 'wizard' | 'rerun';

/* ── SLICE 5 — suggestion-field descriptors ───────────────────────────
 *
 * One descriptor per intent field the Intent-Intelligence layer can
 * suggest. Each descriptor owns THREE responsibilities so the render +
 * accept paths never re-derive field-specific logic:
 *
 *   1. `sourceKeys` — the `fieldSource` provenance key(s) that lock the
 *      field. If ANY is user-authored (`'user'` / `'accepted'` /
 *      `'manual'`) the chip is suppressed — USER ALWAYS WINS.
 *   2. `valueLabel` — formats the suggested value for the chip caption.
 *   3. `applyPatch` — produces the FULL next `ProjectIntent` for an
 *      accept, ALWAYS via the projectIntent.ts writer helpers (so the
 *      MW/bucket + date/bucket pairs never drift) and ALWAYS stamping
 *      `fieldSource = 'accepted'` on every key it owns.
 *
 * `RankableIntentField` is the union the ranker speaks; only the subset
 * that the merged `SuggestedFullIntent` actually carries gets a
 * descriptor. Fields without one (e.g. reactorDesignClass — gated, no
 * deterministic/LLM value today) simply render no chip. */
const USER_AUTHORED_FIELD_SOURCES: ReadonlySet<string> = new Set(['user', 'accepted', 'manual']);

/** A field is locked when ANY of its provenance keys is user-authored. */
function isFieldUserAuthored(
  intent: ProjectIntent,
  ...sourceKeys: string[]
): boolean {
  const src = intent.fieldSource as Record<string, string | undefined> | undefined;
  if (!src) return false;
  return sourceKeys.some((k) => {
    const v = src[k];
    return typeof v === 'string' && USER_AUTHORED_FIELD_SOURCES.has(v);
  });
}

/** Stamp `fieldSource = 'accepted'` on the given keys, preserving the rest. */
function stampAccepted(intent: ProjectIntent, ...keys: string[]): ProjectIntent {
  const fieldSource = { ...(intent.fieldSource ?? {}) } as Record<string, string>;
  for (const k of keys) fieldSource[k] = 'accepted';
  return { ...intent, fieldSource: fieldSource as ProjectIntent['fieldSource'] };
}

interface SuggestionFieldDescriptor {
  /**
   * Key into `SuggestedFullIntent`. Widened to `string` (#34) so the four
   * DECIDE-NEXT BTM decision fields — `capacityCreditIntent`, `offtake`,
   * `carbonCommitment`, `reactorDesignClass` — can carry a descriptor even
   * while the merge layer's `SuggestedFullIntent` does not yet declare a
   * dedicated key for each (they're read via a runtime index + cast below).
   * The original spine keys remain valid `keyof SuggestedFullIntent` values.
   */
  suggestionKey: keyof SuggestedFullIntent | string;
  /** The `data-field-id` row the chip mounts beside (+ test-id suffix). */
  fieldId: string;
  /** Provenance key(s) gating USER-WINS suppression. */
  sourceKeys: string[];
  /** Human-readable label for a suggested value. */
  valueLabel: (value: unknown) => string;
  /** Build the FULL next intent for an accept (writers + provenance stamp). */
  applyPatch: (intent: ProjectIntent, value: unknown) => ProjectIntent;
}

/**
 * Descriptor table keyed by `RankableIntentField`. ADDITIVE — adding a
 * field here lights up its chip + nudge with no other changes. Order is
 * irrelevant (chips render per their host field row).
 */
const SUGGESTION_FIELDS: Partial<Record<RankableIntentField, SuggestionFieldDescriptor>> = {
  workloadProfile: {
    suggestionKey: 'workloadProfile',
    fieldId: 'workloadProfile',
    sourceKeys: ['workloadProfile'],
    valueLabel: (v) => String(v),
    applyPatch: (intent, v) =>
      stampAccepted({ ...intent, workloadProfile: v as ProjectIntent['workloadProfile'] }, 'workloadProfile'),
  },
  coolingStrategy: {
    suggestionKey: 'coolingStrategy',
    fieldId: 'coolingStrategy',
    sourceKeys: ['coolingStrategy'],
    valueLabel: (v) => COOLING_STRATEGY_LABELS[v as NonNullable<ProjectIntent['coolingStrategy']>] ?? String(v),
    applyPatch: (intent, v) =>
      stampAccepted({ ...intent, coolingStrategy: v as ProjectIntent['coolingStrategy'] }, 'coolingStrategy'),
  },
  nuclearInterest: {
    suggestionKey: 'nuclearInterest',
    fieldId: 'nuclearInterest',
    sourceKeys: ['nuclearInterest'],
    valueLabel: (v) => NUCLEAR_INTEREST_LABELS[v as NonNullable<ProjectIntent['nuclearInterest']>] ?? String(v),
    applyPatch: (intent, v) =>
      stampAccepted({ ...intent, nuclearInterest: v as ProjectIntent['nuclearInterest'] }, 'nuclearInterest'),
  },
  priority: {
    suggestionKey: 'priority',
    fieldId: 'priority',
    sourceKeys: ['priority'],
    valueLabel: (v) => {
      const p = String(v);
      return p.charAt(0).toUpperCase() + p.slice(1);
    },
    applyPatch: (intent, v) =>
      stampAccepted({ ...intent, priority: v as ProjectIntent['priority'] }, 'priority'),
  },
  timeline: {
    suggestionKey: 'timeline',
    fieldId: 'timeline',
    // The exact-date sibling shares provenance — locking either locks both.
    sourceKeys: ['timeline', 'inServiceDate'],
    valueLabel: (v) => TIMELINE_LABELS[v as NonNullable<ProjectIntent['timeline']>] ?? String(v),
    applyPatch: (intent, v) =>
      // Range-bucket writer keeps timeline / inServiceDate consistent.
      stampAccepted(withTimelineBucket(intent, v as TimelineBucket), 'timeline', 'inServiceDate'),
  },
  powerRequirement: {
    suggestionKey: 'powerRequirementMW',
    fieldId: 'powerRequirement',
    // MW + bucket share provenance — locking either locks both.
    sourceKeys: ['powerRequirementMW', 'powerRequirement'],
    valueLabel: (v) => `${formatStackNum(Number(v))} MW`,
    applyPatch: (intent, v) => {
      // Clamp to the form's bounds (guardrail) before the writer recomputes
      // the bucket. Out-of-range values are dropped (no write) by the caller
      // — but defensive-clamp here too so a stale suggestion never escapes.
      const mw = Math.min(Math.max(Number(v), POWER_MW_MIN), POWER_MW_MAX);
      return stampAccepted(withPowerRequirementMW(intent, mw), 'powerRequirementMW', 'powerRequirement');
    },
  },
  pueValue: {
    suggestionKey: 'pueValue',
    fieldId: 'pueValue',
    sourceKeys: ['pueValue'],
    valueLabel: (v) => `PUE ${formatStackNum(Number(v))}`,
    applyPatch: (intent, v) =>
      stampAccepted({ ...intent, pueValue: Number(v) }, 'pueValue'),
  },
  bopHeatRejection: {
    suggestionKey: 'bopHeatRejection',
    fieldId: 'bopHeatRejection',
    sourceKeys: ['bopHeatRejection'],
    valueLabel: (v) =>
      BOP_HEAT_REJECTION_OPTIONS.find((o) => o.value === v)?.label ?? String(v),
    // CO-WRITE the paired cold-production MACHINE when the suggested sink carries
    // the overlapping absorption / free-cooling concept (e.g. the inference path
    // emits `absorption-cchp`). Without this the chip-apply path would reintroduce
    // the contradiction the form handlers now prevent. Genuinely-distinct sinks
    // leave the machine axis untouched.
    applyPatch: (intent, v) => {
      const sink = v as BopHeatRejection;
      const patched: ProjectIntent = { ...intent, bopHeatRejection: sink };
      const pairedGen = coolingGenerationForSink(sink);
      if (pairedGen) patched.coolingGeneration = pairedGen;
      return stampAccepted(patched, 'bopHeatRejection');
    },
  },
  // ── #34 — DECIDE-NEXT BTM decision fields. The Stage-1 suggester now emits
  // an inferred suggestion (value/source/rationale/confidence) for each, so
  // these descriptors let the DECIDE-NEXT panel + per-field chip OFFER a
  // concrete value to accept. The value is the INNER enum (e.g. the ELCC
  // `.target`, the offtake `.structure`); applyPatch spread-merges it onto the
  // nested object so sibling keys (priceBasis / queueId) survive — mirroring
  // the grid-&-offtake band's onChange handlers below. carbonCommitment + the
  // reactor class are flat enums. Every accept stamps fieldSource='accepted'.
  capacityCreditIntent: {
    suggestionKey: 'capacityCreditIntent',
    fieldId: 'capacityCreditIntent',
    sourceKeys: ['capacityCreditIntent'],
    valueLabel: (v) => CAPACITY_CREDIT_TARGET_LABELS[v as CapacityCreditTargetId] ?? String(v),
    applyPatch: (intent, v) =>
      stampAccepted(
        {
          ...intent,
          capacityCreditIntent: {
            ...intent.capacityCreditIntent,
            target: v as NonNullable<ProjectIntent['capacityCreditIntent']>['target'],
          },
        },
        'capacityCreditIntent',
      ),
  },
  offtake: {
    suggestionKey: 'offtake',
    fieldId: 'offtake',
    sourceKeys: ['offtake'],
    valueLabel: (v) => OFFTAKE_STRUCTURE_LABELS[v as OfftakeStructureId] ?? String(v),
    applyPatch: (intent, v) =>
      stampAccepted(
        {
          ...intent,
          offtake: {
            ...intent.offtake,
            structure: v as NonNullable<ProjectIntent['offtake']>['structure'],
          },
        },
        'offtake',
      ),
  },
  carbonCommitment: {
    suggestionKey: 'carbonCommitment',
    fieldId: 'carbonCommitment',
    sourceKeys: ['carbonCommitment'],
    valueLabel: (v) => CARBON_COMMITMENT_LABELS[v as CarbonCommitmentId] ?? String(v),
    applyPatch: (intent, v) =>
      stampAccepted(
        { ...intent, carbonCommitment: v as ProjectIntent['carbonCommitment'] },
        'carbonCommitment',
      ),
  },
  reactorDesignClass: {
    suggestionKey: 'reactorDesignClass',
    fieldId: 'reactorDesignClass',
    sourceKeys: ['reactorDesignClass'],
    valueLabel: (v) =>
      REACTOR_DESIGN_CATALOG[v as ReactorDesignClassId]?.label ?? String(v),
    applyPatch: (intent, v) =>
      // Selecting a class invalidates any vendor pinned under another class.
      stampAccepted(
        {
          ...intent,
          reactorDesignClass: v as ReactorDesignClassId,
          reactorVendorId: undefined,
        },
        'reactorDesignClass',
      ),
  },
};

// Declutter (2026-06-10) — `SUGGESTABLE_FIELD_LABEL` + `suggestableFieldLabel`
// were removed alongside the prefill/intelligence banner (their only
// consumers). The per-field suggestion chips render their own labels via
// each descriptor's `valueLabel`, so no shared label map is needed.

export interface ProjectIntentFormProps {
  /** Current intent — pass `undefined` for the empty / "Set up project intent" state. */
  value: ProjectIntent | undefined;
  onChange: (next: ProjectIntent) => void;
  /** Retained for host compatibility; the form no longer exposes a bulk reset. */
  onResetUndecided?: () => void | Promise<void>;
  resetPending?: boolean;
  /** A normal host save must finish before resetting the persisted choices. */
  resetDisabled?: boolean;
  readOnly?: boolean;
  resetExplanation?: string;
  /**
   * Stepper-mode "Done" handler (the last step's footer CTA). When absent the
   * button stays disabled (legacy). The site host wires this to the guided
   * loop's finish (record + return to Path to GO) so completing the stepper
   * lands the user back on the hub with the next recommendation.
   */
  onStepperDone?: () => void;
  /** Test-id prefix so multiple instances can co-exist on a page. */
  testIdPrefix?: string;
  /**
   * NSW1 — render mode. Defaults to 'overview' for back-compat with
   * pre-NSW callers (SiteConfigurationPanel, rerun panel) that didn't
   * pass a mode.
   */
  mode?: ProjectIntentFormMode;
  /** Screening uses approximate inputs; detailed planning keeps every canonical editor available. */
  inputStage?: 'screening' | 'detailed';
  /** Open the separate design-assumptions editor after reviewing recommendations. */
  onOpenDetailedPlanning?: () => void;
  /**
   * NSW1 — cross-field warnings rendered inline beneath the form. Same
   * grammar as CU3's per-prefs warnings so the chip styling matches.
   * BriefMismatchWarnings' description-vs-chip rules flow through here
   * once the wizard's Step 3 is hooked up via NSW2.
   */
  warnings?: CrossFieldWarning[];
  /**
   * NSW1 — handler invoked when the user clicks a warning's
   * suggested-fix button. Receives the chip's `fixField` + value so
   * the caller can merge into the intent. Wizard wires this to flip
   * the chip that triggered the warning.
   */
  onApplyWarningFix?: (fixField: string, value: unknown) => void;
  /** NSW1 — render the provenance-chip column. Default true on overview/rerun, false on wizard. */
  showProvenanceChips?: boolean;
  /** NSW1 — render the live AI-context preview panel. Default true on overview, false on wizard/rerun. */
  showAiContextPreview?: boolean;
  /** NSW1 — render the per-field "affects score" badge. Default true on every mode. */
  showAffectsScoreBadges?: boolean;
  /**
   * SSOT-7 — site-level extracted-claim context (typically
   * `site.siteContext`). When passed, each canonical field that has a
   * mismatched extracted claim renders a small "Site documents
   * suggest: …" notice beneath the input. The detector reuses IC6's
   * `disagreementsForField` so the source-of-truth comparator is the
   * same one the LLM-prompt builder sees. Pass `null` / omit to hide
   * the notices (back-compat: pre-SSOT callers don't supply it).
   */
  siteContext?: SiteContext | null;

  /* ── SLICE 5 — Intent Intelligence wiring (ADDITIVE, all OPTIONAL) ────
   * The deterministic ∪ LLM merged suggestion set + the "what to fill
   * next" ranking, plumbed in by SiteConfigurationPanel via
   * `useIntentIntelligence`. Every prop is optional — pre-Slice-5
   * callers (wizard / rerun) render exactly as before. When present, the
   * form renders per-field confidence chips (gated on fieldSource so a
   * user-authored field NEVER shows a suggestion), a "next input" nudge,
   * and an "Apply all" affordance. Accepts route through the existing
   * `onChange` + the projectIntent.ts writers and stamp `fieldSource` so
   * an accepted value is never re-suggested (USER ALWAYS WINS). */

  /** Merged per-field suggestions (`useIntentIntelligence().suggestions`).
   *  Each entry already excludes user-authored fields at the merge layer;
   *  the form re-asserts the provenance gate before rendering a chip. */
  intentSuggestions?: SuggestedFullIntent;
  /**
   * Per-phase power recommendation (host-computed — the form has no SED /
   * scenario access; SiteConfigurationPanel resolves availability + envelope
   * and runs the engine). Absent → the recommendation block renders nothing
   * (wizard/rerun mounts unaffected).
   */
  phasePowerPlan?: PhasePowerPlanResolution | null;
  /** Staleness of an APPLIED plan (host-computed, ordered first reason). */
  phasePowerPlanStaleReason?: string | null;
  /** Apply handler — must route through the host's existing intent-draft
   *  flow (no new write path). */
  onApplyPhasePowerPlan?: (phaseId: string) => void;
  /** The single highest-value field to fill next
   *  (`useIntentIntelligence().nextBestInput`). Drives the nudge banner. */
  nextBestInput?: NextInputRecommendation | null;
  /** Fields the user dismissed this session — their chips stay hidden
   *  until a re-inference produces a materially different suggestion. */
  dismissedFields?: ReadonlySet<RankableIntentField>;
  /** Accept a single suggestion. The form computes the writer patch +
   *  fieldSource stamp and hands the FULL next intent to the caller so it
   *  can merge into the draft (no new store action). When omitted the
   *  form falls back to its own `onChange` so the panel can stay thin. */
  onAcceptSuggestion?: (field: RankableIntentField, next: ProjectIntent) => void;
  /** Dismiss a single field's suggestion (user said "not now"). */
  onDismissSuggestion?: (field: RankableIntentField) => void;

  /**
   * Focused-Stepper layout (opt-in, PRESENTATION-ONLY). Default `false`
   * preserves the existing single-scroll band layout EXACTLY for every
   * current caller (wizard / rerun / SiteConfigurationPanel's other
   * mounts). When `true`, the SAME field groups, writers, conditionals,
   * suggestion chips and disagreement notices are wrapped in a guided
   * 7-step stepper (Power → Workload → Phasing → Cooling → Timeline →
   * Nuclear → Priority). Every group stays MOUNTED (gated with the
   * `hidden` attribute, never conditionally unmounted) so field state +
   * the Intelligence banner survive step navigation. Saving is still
   * owned by the host footer — the stepper never auto-saves. */
  stepper?: boolean;

  /**
   * Declutter (2026-06-10) — optional pre-built Context surface node
   * (the host's silent `SiteContextInput` element). When provided AND
   * `stepper` is true, the form PREPENDS a "Context" step (step 1) whose
   * panel renders this node under a one-line purpose. Node-passing keeps
   * the context value/extraction wiring owned by the host
   * (SiteConfigurationPanel) — the form never threads context state.
   * Omit (the default) and the stepper renders the canonical field steps
   * only, exactly as before. */
  contextStep?: React.ReactNode;
  /**
   * Declutter (2026-06-10) — best-effort completeness for the prepended
   * Context step (typically `contextText.trim().length > 0`). Drives the
   * step-1 tick + the N/total count. Never blocks navigation. Ignored
   * when `contextStep` is absent. */
  contextStepComplete?: boolean;
  /** Optional controlled active step for stepper mode. */
  activeStep?: StepperStepId;
  /** Optional callback fired whenever the stepper changes steps. */
  onActiveStepChange?: (step: StepperStepId) => void;
  /**
   * Optional step-integrated header rendered inside the stepper card right
   * above the active step's fields (used by the host to integrate Stage 2
   * decisions directly into Steps 1–8).
   */
  renderStepStageHeader?: (ctx: {
    activeStep: StepperStepId;
    onSelectStep: (step: StepperStepId) => void;
  }) => React.ReactNode;
}

/** Stable empty starting point — same shape callers seeded with before SC2-3. */
export function emptyProjectIntent(): ProjectIntent {
  return {
    workloadProfile: 'mixed',
    powerRequirement: 'undecided',
    coolingStrategy: 'undecided',
    nuclearInterest: 'undecided',
    timeline: 'undecided',
    priority: 'undecided',
  };
}

/** NSW1 — small section header used for the three field bands. Keeps the
 *  typography parity hint that the spec calls for (10px text-muted
 *  uppercase tracking-widest). */
const FieldBandHeader: React.FC<{ label: string; helper?: string }> = ({ label, helper }) => (
  <div className="space-y-0.5">
    <div className="type-meta text-text-muted font-bold">{label}</div>
    {helper && <div className="text-[10px] italic text-text-muted">{helper}</div>}
  </div>
);

export const ProjectIntentForm: React.FC<ProjectIntentFormProps> = ({
  value,
  onChange,
  resetPending = false,
  resetDisabled = false,
  readOnly = false,
  onStepperDone,
  testIdPrefix = 'project-intent-form',
  mode = 'overview',
  inputStage = 'detailed',
  onOpenDetailedPlanning,
  warnings,
  onApplyWarningFix,
  showProvenanceChips,
  showAiContextPreview,
  showAffectsScoreBadges,
  siteContext,
  intentSuggestions,
  nextBestInput,
  dismissedFields,
  onAcceptSuggestion,
  onDismissSuggestion,
  phasePowerPlan,
  phasePowerPlanStaleReason,
  onApplyPhasePowerPlan,
  stepper = false,
  contextStep,
  contextStepComplete = false,
  activeStep: controlledActiveStep,
  onActiveStepChange,
  renderStepStageHeader,
}) => {
  // Start from the empty baseline so newly toggled rows don't crash on
  // a `ProjectIntent` that lacks a key. Spread the supplied value on top
  // so any populated keys win.
  const intent: ProjectIntent = { ...emptyProjectIntent(), ...(value ?? {}) };
  const workloadExplicitUnknown = isIntentFieldExplicitlyUnknown(
    value,
    'workloadProfile',
  );
  // Firestore omits undefined object properties. The provenance marker is the
  // durable representation of a deliberately unknown workload, so never let
  // the legacy `mixed` new-project default overlay that state on reload. This
  // also keeps unrelated edits from writing `mixed` back as a factual answer.
  if (workloadExplicitUnknown) delete intent.workloadProfile;

  // Focused-Stepper local navigation state. Defaults to the first step —
  // `context` when a Context step is prepended, else `power`. Purely UI —
  // never persisted; reset on remount is fine. Inert when `stepper` is
  // false.
  const [internalActiveStep, setInternalActiveStep] = useState<StepperStepId>(
    contextStep != null ? 'context' : STEPPER_STEPS[0].id,
  );
  const activeStep = controlledActiveStep ?? internalActiveStep;
  const setActiveStep = (next: StepperStepId) => {
    setInternalActiveStep(next);
    onActiveStepChange?.(next);
  };

  // NSW1 — companion-component defaults derived from the mode. Explicit
  // props always win so callers can override on a case-by-case basis.
  // Declutter (2026-06-10) — the AI context preview card was removed, so
  // `showAiContextPreview` no longer drives any render. Kept inert (the
  // prop stays on the interface for caller back-compat).
  const _showAiContextPreview = showAiContextPreview ?? mode === 'overview';
  void _showAiContextPreview;
  // Declutter (2026-06-10) — `nextBestInput` previously fed the removed top
  // "Suggested next" banner. The banner was REINSTATED behind a runtime flag
  // (Intent Intelligence Slice 2/5): when `isIntentNextBestInputEnabled()` is
  // OFF (the prod default) the prop is consumed inertly via `void` below and
  // the render is byte-identical to the post-declutter form. When ON, the
  // `nextInputNudge` element (built later, after `acceptSuggestion` /
  // `offerableSuggestions` are in scope) re-surfaces the top recommendation.
  const nextInputEnabled = isIntentNextBestInputEnabled();
  if (!nextInputEnabled) {
    void nextBestInput;
  }
  // showProvenanceChips + showAffectsScoreBadges currently have no
  // dedicated UI to render — they're API placeholders for the SC2-style
  // provenance chips + AffectsScoreBadge mounts that will land in a
  // follow-up. Default values follow the spec's matrix; consumed only
  // by the parity test which asserts the props are accepted.
  const _showProvenanceChips = showProvenanceChips ?? mode !== 'wizard';
  const _showAffectsScoreBadges = showAffectsScoreBadges ?? true;
  void _showProvenanceChips; void _showAffectsScoreBadges;

  const isWizard = mode === 'wizard';
  const isScreening = inputStage === 'screening';

  // The owner's own words about what the MW figure means — offered as an
  // accept-on-click chip, never applied silently.
  const powerBasisHint = useMemo(() => inferPowerBasisFromText([
    siteContext?.text,
    ...(siteContext?.claims ?? []).map(claim => `${claim.claim} ${claim.claimedValue ?? ''}`),
  ]), [siteContext]);

  /* ── SSOT-7 — IC6 disagreement detector ───────────────────────────
   * Compute SiteContext-extracted-claim drift per canonical intent
   * field. Memoised so the detector doesn't fire on every keystroke
   * in an unrelated field. */
  const disagreements = useMemo(() => {
    if (!siteContext) {
      return {} as Record<InputFieldId, ReturnType<typeof disagreementsForField>>;
    }
    const sources = { projectIntent: intent, siteContext };
    const out: Partial<Record<InputFieldId, ReturnType<typeof disagreementsForField>>> = {};
    const fields: InputFieldId[] = ['projectMW', 'inServiceDate', 'cooling', 'powerSource', 'customerArchetype', 'riskPosture'];
    for (const f of fields) {
      const d = disagreementsForField(f, sources);
      if (d.length > 0) out[f] = d;
    }
    return out as Record<InputFieldId, ReturnType<typeof disagreementsForField>>;
  }, [intent, siteContext]);

  /** Render an IC6 disagreement notice block when `fieldId` has any
   *  SiteContext-vs-Intent drift. Returns `null` otherwise so it can
   *  be unconditionally embedded under each field row. */
  const renderDisagreement = (fieldId: InputFieldId): React.ReactElement | null => {
    const items = disagreements[fieldId];
    if (!items || items.length === 0) return null;
    return (
      <div
        data-testid={`${testIdPrefix}-disagreement-${fieldId}`}
        className="text-[10px] text-warning bg-warning/10 border border-warning/30 px-2 py-1 mt-1 rounded"
      >
        Site documents suggest: {items.map(d => `${d.losingLayer} says ${d.losingDisplay}`).join(' · ')}. Resolve in Site Evidence tab.
      </div>
    );
  };

  /* ── SLICE 5 — Intent-Intelligence suggestion plumbing ────────────
   *
   * Resolve, per rankable field, whether we have an OFFERABLE suggestion
   * right now: a merged suggestion exists, the field is NOT user-authored
   * (USER ALWAYS WINS), the field is NOT dismissed this session, and the
   * suggestion is not a bare platform fallback. The result drives both
   * the per-field chip and the "next input" nudge — one source of truth.
   *
   * Numeric guardrail: a `powerRequirementMW` suggestion outside the
   * form's 0.1–10000 MW bounds is treated as no-suggestion (dropped),
   * mirroring the manual-entry validation — we never offer a value the
   * form itself would reject. */
  const offerableSuggestions = useMemo(() => {
    const out: Partial<Record<RankableIntentField, { descriptor: SuggestionFieldDescriptor; suggestion: IntentSuggestion<unknown> }>> = {};
    if (!intentSuggestions) return out;

    (Object.keys(SUGGESTION_FIELDS) as RankableIntentField[]).forEach((field) => {
      const descriptor = SUGGESTION_FIELDS[field];
      if (!descriptor) return;
      if (dismissedFields?.has(field)) return;
      // USER ALWAYS WINS — a user-authored field shows NO suggestion.
      if (isFieldUserAuthored(intent, ...descriptor.sourceKeys)) return;

      // `suggestionKey` is a `string` (widened in #34 so the BTM fields can
      // carry descriptors before the merge type declares dedicated keys), so
      // index through a record view rather than the closed `SuggestedFullIntent`.
      const suggestion = (intentSuggestions as Record<string, IntentSuggestion<unknown> | undefined>)[
        descriptor.suggestionKey
      ];
      if (!suggestion) return;
      // A bare platform fallback carries no real signal — don't surface it.
      if (suggestion.source === 'fallback') return;

      // Numeric guardrail for MW — drop out-of-range suggestions entirely.
      if (descriptor.suggestionKey === 'powerRequirementMW') {
        const mw = Number(suggestion.value);
        if (!Number.isFinite(mw) || mw < POWER_MW_MIN || mw > POWER_MW_MAX) return;
      }

      out[field] = { descriptor, suggestion };
    });
    return out;
  }, [intentSuggestions, dismissedFields, intent]);

  /** Accept a single field's suggestion: build the writer-stamped patch and
   *  route it through the caller's `onAcceptSuggestion` (preferred) or the
   *  plain `onChange` fallback. Either way the value lands + `fieldSource`
   *  is stamped `'accepted'`, so the field is never re-suggested. */
  const acceptSuggestion = (field: RankableIntentField) => {
    const entry = offerableSuggestions[field];
    if (!entry) return;
    const next = entry.descriptor.applyPatch(intent, entry.suggestion.value);
    if (onAcceptSuggestion) onAcceptSuggestion(field, next);
    else onChange(next);
  };

  /** Render the per-field confidence chip beside a suggestable field row.
   *  Returns `null` when nothing is offerable for `field` (e.g. the user
   *  authored it, it was dismissed, or no signal exists). */
  const renderSuggestionChip = (field: RankableIntentField): React.ReactElement | null => {
    const entry = offerableSuggestions[field];
    if (!entry) return null;
    const { descriptor, suggestion } = entry;
    return (
      <div
        className="flex items-center gap-2 pt-1"
        data-testid={`${testIdPrefix}-suggestion-${field}`}
        data-source={suggestion.source}
      >
        <span className="type-meta uppercase tracking-widest text-text-muted">Suggested</span>
        <SuggestionChip
          value={String(suggestion.value)}
          valueLabel={descriptor.valueLabel(suggestion.value)}
          rationale={suggestion.rationale}
          confidence={suggestion.confidence}
          source={suggestion.source as IntentSuggestionSource}
          onAccept={() => acceptSuggestion(field)}
          testId={`${testIdPrefix}-suggestion-chip-${field}`}
        />
        {onDismissSuggestion && (
          <button
            type="button"
            onClick={() => onDismissSuggestion(field)}
            aria-label={`Dismiss suggestion for ${field}`}
            data-testid={`${testIdPrefix}-suggestion-dismiss-${field}`}
            className="type-meta uppercase tracking-widest text-text-muted hover:text-text-secondary"
          >
            Dismiss
          </button>
        )}
      </div>
    );
  };

  /* ── Intelligence banner — REINSTATED behind a runtime flag ───────
   * The top "Suggested next" nudge was decluttered on 2026-06-10. It is
   * REINSTATED here behind `isIntentNextBestInputEnabled()` (Intent
   * Intelligence Slice 2/5). Default OFF → `nextInputNudge` is `null`, so
   * the render below is byte-identical to the post-declutter form.
   *
   * When ON and `nextBestInput` carries a recommendation, the nudge surfaces
   * the single highest-value undecided field (ranked by `rankNextInputs`)
   * with its reason. If an OFFERABLE (non-`fallback`, context-grounded)
   * suggestion exists for that same field, the nudge also offers a one-click
   * Accept that routes through the SAME `acceptSuggestion` path as the
   * per-field chip — the writer stamps `fieldSource='accepted'`, so the
   * value is never re-nudged and a user-authored field is never clobbered.
   * The nudge NEVER asserts an unverified value: with no offerable
   * suggestion it shows only the "decide next" prompt + reason, no value.
   *
   * Per-field inline suggestion chips (`renderSuggestionChip`) + the IC6
   * disagreement notices render beside each field regardless of this flag. */
  const nextInputNudge: React.ReactElement | null = (() => {
    if (!nextInputEnabled || !nextBestInput) return null;
    // USER ALWAYS WINS — never nudge a field the user has authored. The
    // ranker already skips decided fields, but re-assert here so the nudge is
    // a falsifiable choke point identical to `offerableSuggestions`.
    const descriptor = SUGGESTION_FIELDS[nextBestInput.field];
    if (descriptor && isFieldUserAuthored(intent, ...descriptor.sourceKeys)) return null;

    const fieldLabel = NEXT_INPUT_FIELD_LABEL[nextBestInput.field] ?? nextBestInput.field;
    // Only offer an Accept when we have a real, context-grounded suggestion
    // for THIS field (the same offerable gate the chip uses — drops fallbacks
    // and out-of-range MW). Otherwise the nudge is decide-only (no value).
    const offerable = offerableSuggestions[nextBestInput.field];
    const offeredValueLabel = offerable
      ? offerable.descriptor.valueLabel(offerable.suggestion.value)
      : null;

    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
        data-testid={`${testIdPrefix}-next-input-nudge`}
        data-field={nextBestInput.field}
      >
        <span className="type-meta uppercase tracking-widest text-primary font-bold">
          Suggested next
        </span>
        <span className="type-body text-text-primary font-semibold">{fieldLabel}</span>
        <span className="type-meta text-text-secondary basis-full sm:basis-auto">
          {nextBestInput.reason}
        </span>
        {offerable && offeredValueLabel != null && (
          <button
            type="button"
            onClick={() => acceptSuggestion(nextBestInput.field)}
            data-testid={`${testIdPrefix}-next-input-accept`}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-primary px-2.5 py-1 type-meta uppercase tracking-widest font-bold text-primary hover:bg-primary/10"
          >
            <Check className="h-3.5 w-3.5" />
            Use {offeredValueLabel}
          </button>
        )}
      </div>
    );
  })();

  /* ── Field GROUPS (extracted, defined exactly once) ───────────────
   * Each canonical field group's JSX is defined as a single `const` so
   * BOTH the legacy scroll layout (bands) AND the Focused-Stepper layout
   * compose the SAME element — guaranteeing byte-identical fields,
   * writers, conditionals and data contract across the two layouts. The
   * only thing that differs between layouts is the chrome that wraps
   * these groups. */
  const workloadGroup = (
    <div className="space-y-1.5" data-field-id="workloadProfile">
      <div className="type-meta text-text-muted">Workload profile</div>
      <WorkloadProfilePicker
        value={intent.workloadProfile}
        explicitUnknown={workloadExplicitUnknown}
        // The deprecated `projectType` is cleared with it, as in the Path to
        // GO editor: readers that fall back `workloadProfile ?? projectType`
        // would otherwise resurrect a stale concrete type past the unknown.
        onUnknown={() => onChange(markIntentFieldExplicitlyUnknown(
          { ...intent, workloadProfile: undefined, projectType: undefined },
          'workloadProfile',
        ))}
        onChange={(p) => onChange(clearIntentFieldExplicitlyUnknown(
          { ...intent, workloadProfile: p },
          'workloadProfile',
        ))}
        layout="pills"
      />
      {renderSuggestionChip('workloadProfile')}
      {renderDisagreement('customerArchetype')}
      {/* Reference comparison of the key planning assumptions behind every
          template — collapsed by default so it never crowds the picker.
          Answers "how do AI-training/inference and colocation actually differ
          at similar scale + dev fee?" without leaving the intent step. */}
      {!isScreening && <WorkloadAssumptionsTable />}
      {/* Cited engineering + BTM-power reference (rack density, cooling, PUE,
          and how the power source and workload constrain each other). */}
      {!isScreening && <WorkloadEngineeringReference />}
    </div>
  );

  const powerGroup = (
    <div data-field-id="powerRequirement">
      <PowerRequirementField
        intent={intent}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
      />
      {renderSuggestionChip('powerRequirement')}
      <PowerBasisAwarenessFields
        intent={intent}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        screening={isScreening}
      />
      {powerBasisHint && intent.powerBasis == null && !isIntentFieldExplicitlyUnknown(intent, 'powerBasis') && (
        <div
          className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5"
          data-testid={`${testIdPrefix}-power-basis-hint`}
        >
          <span className="text-[11px] text-text-primary">
            Your site context says &ldquo;{powerBasisHint.phrase}&rdquo;. Declaring the basis lets screening use one case instead of two.
          </span>
          <button
            type="button"
            onClick={() => onChange({
              ...clearIntentFieldExplicitlyUnknown(intent, 'powerBasis'),
              powerBasis: powerBasisHint.basis,
              fieldSource: { ...(intent.fieldSource ?? {}), powerBasis: 'accepted' } as ProjectIntent['fieldSource'],
            })}
            className="rounded-md border border-primary px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
            data-testid={`${testIdPrefix}-power-basis-hint-accept`}
          >
            Use {powerBasisHint.basis === 'it-load' ? 'IT load' : 'facility load'}
          </button>
        </div>
      )}
      {!isScreening && <AnnualDemandUtilizationField
        intent={intent}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        siteContext={siteContext}
      />}
      {!isScreening && renderSuggestionChip('pueValue')}
      {/* bopHeatRejection chip moved with its selector into the clustered
          Cooling section below the phases editor (CHANGE 3). */}
      {renderDisagreement('projectMW')}
    </div>
  );

  /* ── Score-affecting band ─────────────────────────────────────────
   * Workload profile + power requirement drive the LCOE / readiness
   * scoring most heavily, so they're always visible up top regardless
   * of mode. */
  const scoreAffectingBand = (
    <div className="space-y-3" data-field-band="score-affecting">
      <FieldBandHeader label="Score-affecting" helper={isWizard ? 'Always visible — these drive every report.' : undefined} />
      {workloadGroup}
      {powerGroup}
    </div>
  );

  /* ── Recommended band ─────────────────────────────────────────────
   * Phased rollout + the clustered Cooling section + timeline + nuclear
   * interest are recommended to fill in — they meaningfully tailor the
   * report but the form still functions without them.
   *
   * CHANGE 3 — the three cooling axes are grouped into ONE Cooling section
   * positioned AFTER the phases editor, so the R-COOL-VALIDITY gating reads
   * the phase power mix entered above it. CHANGE 4 — the IT cooling loop
   * (how heat leaves the SERVERS) is labelled and visually separated from the
   * BTM / site cooling axes (how heat leaves the SITE) so the two are never
   * confused. */
  const phasesGroup = (
    <div data-field-id="phases">
      <PhasedRolloutEditor
        intent={intent}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        phasePowerPlan={phasePowerPlan}
        phasePowerPlanStaleReason={phasePowerPlanStaleReason}
        onApplyPhasePowerPlan={onApplyPhasePowerPlan}
      />
    </div>
  );

  /* Clustered Cooling section — rendered AFTER the phases editor (CHANGE 3).
     Two clearly-separated groups (CHANGE 4):
       1. IT cooling loop  — how heat leaves the SERVERS (rack-side capture)
       2. Site / BTM cooling — how heat leaves the SITE (cold end + BTM cold
          production), via `CoolingAxesSection`. */
  const coolingGroup = (
      <div
        className="space-y-3 border border-border-subtle rounded-lg p-3"
        data-field-id="cooling-section"
        data-testid={`${testIdPrefix}-cooling-section`}
      >
        <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
          Cooling
        </div>

        {/* 1 — IT cooling loop (the existing site-level `coolingStrategy`). */}
        <div className="space-y-1.5" data-field-id="coolingStrategy">
          <div className="type-meta text-text-muted font-bold">
            IT cooling loop
            <span className="normal-case tracking-normal text-text-secondary ml-2">
              how heat leaves the servers
            </span>
          </div>
          <p className="text-[10px] italic text-text-muted leading-relaxed normal-case tracking-normal">
            The rack / server-side heat-capture loop (air vs liquid). This is the
            IT loop only — not the BTM cooling axes below.
          </p>
          <div className="flex flex-wrap gap-2">
            {(['air', 'liquid', 'hybrid', 'undecided'] as const).map(c => (
              <PillButton
                key={c}
                active={intent.coolingStrategy === c}
                onClick={() => {
                  const next = { ...intent, coolingStrategy: c };
                  onChange(c === 'undecided'
                    ? markIntentFieldExplicitlyUnknown(next, 'coolingStrategy')
                    : clearIntentFieldExplicitlyUnknown(next, 'coolingStrategy'));
                }}
                testId={`${testIdPrefix}-cooling-${c}`}
              >
                {COOLING_STRATEGY_LABELS[c]}
              </PillButton>
            ))}
          </div>
          {renderSuggestionChip('coolingStrategy')}
          {renderDisagreement('cooling')}
        </div>

        {/* 2 — Site / BTM cooling axes (heat rejection + BTM cold production). */}
        <CoolingAxesSection
          intent={intent}
          onChange={onChange}
          testIdPrefix={testIdPrefix}
        />
        {renderSuggestionChip('bopHeatRejection')}
      </div>
  );

  const timelineGroup = (
    <div data-field-id="timeline">
      <TimelineField
        intent={intent}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        phases={intent.phases}
      />
      {renderSuggestionChip('timeline')}
      {renderDisagreement('inServiceDate')}
    </div>
  );

  const nuclearGroup = (
    <>
      <div className="space-y-1.5" data-field-id="nuclearInterest">
        <div className="type-meta text-text-muted">Nuclear interest</div>
        <div className="flex flex-wrap gap-2">
          {(['primary', 'considering', 'no', 'undecided'] as const).map(n => (
            <PillButton
              key={n}
              active={intent.nuclearInterest === n}
              onClick={() => {
                let next: ProjectIntent = {
                  ...intent,
                  nuclearInterest: n,
                  ...(n === 'no' || n === 'undecided'
                    ? { reactorDesignClass: undefined, reactorVendorId: undefined }
                    : {}),
                };
                if (n === 'no' || n === 'undecided') {
                  next = clearIntentFieldExplicitlyUnknown(next, 'reactorDesignClass');
                }
                onChange(n === 'undecided'
                  ? markIntentFieldExplicitlyUnknown(next, 'nuclearInterest')
                  : clearIntentFieldExplicitlyUnknown(next, 'nuclearInterest'));
              }}
              testId={`${testIdPrefix}-nuclear-${n}`}
            >
              {NUCLEAR_INTEREST_LABELS[n]}
            </PillButton>
          ))}
        </div>
        {renderSuggestionChip('nuclearInterest')}
        {renderDisagreement('powerSource')}
      </div>

      {intentShowsReactorDesignPicker(intent) && (
        <div className="space-y-1.5" data-field-id="reactorDesignClass">
          <div className="type-meta text-text-muted">Reactor design class</div>
          <div className="flex flex-wrap gap-2">
            {REACTOR_DESIGN_CLASS_IDS.filter(id => id !== 'undecided').map(classId => (
              <PillButton
                key={classId}
                active={intent.reactorDesignClass === classId}
                onClick={() => onChange(clearIntentFieldExplicitlyUnknown({
                  ...intent,
                  reactorDesignClass: classId,
                  reactorVendorId: undefined,
                }, 'reactorDesignClass'))}
                testId={`${testIdPrefix}-reactor-class-${classId}`}
              >
                {REACTOR_DESIGN_CATALOG[classId].label}
              </PillButton>
            ))}
            <PillButton
              active={!intent.reactorDesignClass || intent.reactorDesignClass === 'undecided'}
              onClick={() => onChange(markIntentFieldExplicitlyUnknown(
                { ...intent, reactorDesignClass: 'undecided', reactorVendorId: undefined },
                'reactorDesignClass',
              ))}
              testId={`${testIdPrefix}-reactor-class-undecided`}
            >
              Not selected
            </PillButton>
          </div>
          <p
            className="type-meta text-text-muted normal-case tracking-normal m-0"
            data-testid="reactor-design-v2-callout"
          >
            {REACTOR_DESIGN_V1_SCOPE_NOTE}
          </p>
          {renderSuggestionChip('reactorDesignClass')}
          {(() => {
            const classId = intent.reactorDesignClass && intent.reactorDesignClass !== 'undecided'
              ? intent.reactorDesignClass
              : null;
            const vendors = classId ? vendorsForClass(classId) : [];
            if (!classId || vendors.length === 0) return null;
            return (
              <div className="space-y-1.5 pt-1" data-field-id="reactorVendorId">
                <div className="type-meta text-text-muted">Specific design (optional)</div>
                <div className="flex flex-wrap gap-2">
                  {vendors.map(v => (
                    <PillButton
                      key={v.id}
                      active={intent.reactorVendorId === v.id}
                      onClick={() => onChange({ ...intent, reactorVendorId: v.id })}
                      testId={`${testIdPrefix}-reactor-vendor-${v.id}`}
                    >
                      {v.label}
                    </PillButton>
                  ))}
                  <PillButton
                    active={!intent.reactorVendorId}
                    onClick={() => onChange({ ...intent, reactorVendorId: undefined })}
                    testId={`${testIdPrefix}-reactor-vendor-none`}
                  >
                    Class only
                  </PillButton>
                </div>
              </div>
            );
          })()}
          {(() => {
            const selected = intent.reactorDesignClass && intent.reactorDesignClass !== 'undecided'
              ? REACTOR_DESIGN_CATALOG[intent.reactorDesignClass as ReactorDesignClassId]
              : null;
            if (!selected) {
              return (
                <div className="type-meta text-text-muted normal-case tracking-normal">
                  Select a generic reactor class to tailor licensing, fuel-cycle, and HALEU findings.
                </div>
              );
            }
            return (
              <div
                className="type-meta text-text-secondary normal-case tracking-normal"
                data-testid={`${testIdPrefix}-reactor-class-summary`}
              >
                {selected.fuelClass} fuel · typical COD {selected.typicalCodYears.low}–{selected.typicalCodYears.high} yr · {selected.licensingPathwaySummary.slice(0, 120)}…
              </div>
            );
          })()}
        </div>
      )}
    </>
  );

  /* ── Recommended band ─────────────────────────────────────────────
   * Phased rollout + the clustered Cooling section + timeline + nuclear
   * interest are recommended to fill in. Composed from the extracted
   * field groups above so scroll + stepper layouts share one definition. */
  const recommendedBand = (
    <div className="space-y-3" data-field-band="recommended">
      <FieldBandHeader label="Recommended" helper={isWizard ? 'Refines tailoring; safe to skip.' : undefined} />
      {phasesGroup}
      {coolingGroup}
      {timelineGroup}
      {nuclearGroup}
    </div>
  );

  /* ── Optional band ────────────────────────────────────────────────
   * Priority is the only optional field today. The band is wired up so
   * future packs (risk tolerance, etc.) can plug fields in without
   * re-touching the wizard / overview mount sites. NSW3 collapses this
   * to a `<details>` in wizard mode. */
  const optionalBandInner = (
    <div className="space-y-3" data-field-band-inner="optional">
      <div className="space-y-1.5" data-field-id="priority">
        <div className="type-meta text-text-muted">Priority</div>
        <div className="flex flex-wrap gap-2">
          {(['cost', 'speed', 'sustainability', 'resilience', 'undecided'] as const).map(p => (
            <PillButton
              key={p}
              active={intent.priority === p}
              onClick={() => {
                const next = { ...intent, priority: p };
                onChange(p === 'undecided'
                  ? markIntentFieldExplicitlyUnknown(next, 'priority')
                  : clearIntentFieldExplicitlyUnknown(next, 'priority'));
              }}
              testId={`${testIdPrefix}-priority-${p}`}
            >
              {p === 'undecided' ? 'Undecided'
                : p === 'cost' ? 'Cost'
                : p === 'speed' ? 'Speed'
                : p === 'sustainability' ? 'Sustainability'
                : 'Resilience'}
            </PillButton>
          ))}
        </div>
        {renderSuggestionChip('priority')}
        {renderDisagreement('riskPosture')}
      </div>
    </div>
  );

  /* NSW3 — wizard mode collapses the optional band into a
   *  `<details>` expander so Step 3 stays fast without dropping any
   *  fields. Same field membership as overview/rerun; just collapsed
   *  by default. Users can click to expand if they want to set a
   *  value during creation; otherwise defaults flow through and they
   *  refine on the site page (matching the existing "refine on the
   *  site page" UX). Overview / rerun render the band open by
   *  default — no expander friction on the canonical edit surface. */
  const optionalFieldCount = 1; // priority — extend as new optional fields land
  const optionalBandSummary = isWizard
    ? `+ ${optionalFieldCount} more option${optionalFieldCount === 1 ? '' : 's'} (priority)`
    : 'Optional';
  const optionalBand = (
    <details
      className="border border-border-subtle rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-optional-band`}
      data-field-band="optional"
      // `open` is a presence attribute — present (any value) ⇒ open,
      // absent ⇒ closed. We pass `open` conditionally so wizard mode
      // gets the closed state by default.
      {...(isWizard ? {} : { open: true })}
    >
      <summary className="cursor-pointer px-3 py-2 type-meta text-text-secondary hover:bg-state-hover font-bold">
        {optionalBandSummary}
      </summary>
      <div className="px-3 pb-3 pt-2 border-t border-border-subtle">
        {optionalBandInner}
      </div>
    </details>
  );

  /* ── Operator attestations (R-131-EPISTEMIC-GROUNDING-E) ───────── */
  const ATTESTATION_STATE_LABELS: Record<AttestationState, string> = {
    yes: 'Yes',
    no: 'No',
    'in-discussion': 'In discussion',
    'in-progress': 'In progress',
    unknown: 'Unknown',
  };

  const setAttestation = (key: string, state: AttestationState | undefined) => {
    const next = { ...(intent.attestations ?? {}) };
    if (state === undefined) {
      delete next[key];
    } else {
      next[key] = state;
    }
    const attestations = Object.keys(next).length > 0 ? next : undefined;
    onChange({ ...intent, attestations });
  };

  const attestationsHasValue = Boolean(
    intent.attestations && Object.values(intent.attestations).some(v => v && v !== 'unknown'),
  );
  const attestationsBandOpen = !isWizard || attestationsHasValue;
  const attestationsBand = (
    <details
      className="border border-border-subtle rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-attestations-band`}
      data-field-band="attestations"
      {...(attestationsBandOpen ? { open: true } : {})}
    >
      <summary
        className="cursor-pointer px-3 py-2 type-meta text-text-secondary hover:bg-state-hover font-bold"
        data-testid={`${testIdPrefix}-attestations-toggle`}
      >
        {isWizard
          ? '+ Operator attestations (known facts not in docs)'
          : 'Operator attestations'}
      </summary>
      <div
        className="px-3 pb-3 pt-2 border-t border-border-subtle space-y-3"
        data-testid={`${testIdPrefix}-attestations-fields`}
      >
        <p className="text-[11px] text-text-muted leading-relaxed">
          Pre-attest factual states you know but are not in uploaded documents. &quot;No&quot; surfaces as a
          confirmed red flag with your attestation as evidence; &quot;Unknown&quot; becomes an open question.
        </p>
        {PROJECT_INTENT_ATTESTATION_DEFS.map(def => {
          const current = intent.attestations?.[def.key];
          return (
            <div key={def.key} className="space-y-1" data-field-id={`attestations.${def.key}`}>
              <div className="type-meta text-text-muted">{def.label}</div>
              <div className="flex flex-wrap gap-1">
                {def.enumValues.map(state => (
                  <PillButton
                    key={state}
                    active={current === state}
                    onClick={() => setAttestation(def.key, current === state ? undefined : state)}
                    testId={`${testIdPrefix}-attestation-${def.key}-${state}`}
                  >
                    {ATTESTATION_STATE_LABELS[state]}
                  </PillButton>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </details>
  );

  /* ── Expanded project-profile band (IC8 rebuild) ─────────────────
   * Surfaces the PP-pack expanded `ProjectIntent` fields (pueTarget,
   * datacenterTierTarget, carbonCommitment, counterparty) inside the
   * SHARED ProjectIntentForm so wizard Step 3 + Overview both get the
   * same edit surface for free (NSW promise). Same `<details>`
   * collapse rule as the optional band: collapsed by default in wizard
   * mode (Step 3 stays one-screen), open by default in overview /
   * rerun (canonical edit surfaces — no expander friction).
   *
   * The 6 remaining PP fields (siteSponsor, geographicConstraints,
   * redundancyClass, modularReplicableFlag, laborPoolPreference,
   * decommissioningPlanProvided) stay deferred — same partial-IC8
   * scope as the original 017a2c8 commit. Adding more rows is
   * mechanical and lands as use cases come up. */
  const expandedProfileHasValue = Boolean(
    intent.datacenterTierTarget
    || intent.redundancyClass
    || intent.carbonCommitment
    || isIntentFieldExplicitlyUnknown(intent, 'carbonCommitment'),
  );
  // Auto-expand when any expanded field already carries a value, even
  // in wizard mode — the user is mid-edit on a non-default field, so
  // hiding it behind a collapsed expander would be a footgun.
  const expandedBandOpen = !isWizard || expandedProfileHasValue;
  const expandedProfileBand = (
    <details
      className="border border-border-subtle rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-expanded-band`}
      data-field-band="expanded-profile"
      {...(expandedBandOpen ? { open: true } : {})}
    >
      <summary
        className="cursor-pointer px-3 py-2 type-meta text-text-secondary hover:bg-state-hover font-bold"
        data-testid={`${testIdPrefix}-expanded-toggle`}
      >
        {isWizard
          ? '+ Project profile (refine on site page)'
          : 'Project profile'}
      </summary>
      <div
        className="px-3 pb-3 pt-2 border-t border-border-subtle space-y-3"
        data-testid={`${testIdPrefix}-expanded-fields`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1" data-field-id="datacenterTierTarget">
            <div className="type-meta text-text-muted">Datacenter tier target</div>
            <select
              value={intent.datacenterTierTarget ?? ''}
              onChange={(e) => onChange({
                ...intent,
                datacenterTierTarget: (e.target.value || undefined) as ProjectIntent['datacenterTierTarget'],
              })}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-datacenterTierTarget`}
            >
              <option value="">(not set)</option>
              <option value="I">Tier I</option>
              <option value="II">Tier II</option>
              <option value="III">Tier III</option>
              <option value="IV">Tier IV</option>
            </select>
          </div>
          <div className="space-y-1" data-field-id="redundancyClass">
            <div className="type-meta text-text-muted">Redundancy class</div>
            <select
              value={intent.redundancyClass ?? ''}
              onChange={(e) => onChange({
                ...intent,
                redundancyClass: (e.target.value || undefined) as ProjectIntent['redundancyClass'],
              })}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-redundancyClass`}
            >
              <option value="">(not set)</option>
              <option value="N">N (no redundancy)</option>
              <option value="N+1">N+1</option>
              <option value="2N">2N</option>
              <option value="2N+1">2N+1</option>
            </select>
          </div>
          <div className="space-y-1" data-field-id="carbonCommitment">
            <div className="type-meta text-text-muted">Carbon commitment</div>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'carbonCommitment')
                ? EXPLICIT_UNKNOWN_SELECT_VALUE
                : (intent.carbonCommitment ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  carbonCommitment: (raw && raw !== EXPLICIT_UNKNOWN_SELECT_VALUE
                    ? raw
                    : undefined) as ProjectIntent['carbonCommitment'],
                };
                onChange(raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                  ? markIntentFieldExplicitlyUnknown(next, 'carbonCommitment')
                  : clearIntentFieldExplicitlyUnknown(next, 'carbonCommitment'));
              }}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-carbonCommitment`}
            >
              <option value="">(not set)</option>
              <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
              <option value="none">None</option>
              <option value="re100">RE100</option>
              <option value="24-7-cfe">24/7 CFE</option>
              <option value="net-zero-2030">Net-zero 2030</option>
              <option value="net-zero-2050">Net-zero 2050</option>
              <option value="custom">Custom</option>
            </select>
            {renderSuggestionChip('carbonCommitment')}
          </div>
        </div>
      </div>
    </details>
  );

  /* ── Grid & offtake band (P1 — field-architecture audit §3.6/§4) ──
   * SIX net-new canonical BTM-feasibility intent fields, grouped into two
   * sensible bands. This band carries the grid / commercial inputs:
   * interconnectionStatus, fuelAccess, offtake, capacityCreditIntent. All
   * OPTIONAL; nested-object writes preserve the other keys on the object.
   * Same `<details>` collapse rule as the expanded-profile band. */
  const gridOfftakeHasValue = Boolean(
    (intent.interconnectionStatus?.queueStatus && intent.interconnectionStatus.queueStatus !== 'none')
    || (intent.fuelAccess?.supply && intent.fuelAccess.supply !== 'none')
    || intent.offtake?.structure
    || (intent.capacityCreditIntent?.target && intent.capacityCreditIntent.target !== 'none')
    || intent.electricitySupply?.arrangement
    || ['interconnectionStatus', 'fuelAccess', 'offtake', 'capacityCreditIntent', 'electricitySupply']
      .some(field => isIntentFieldExplicitlyUnknown(intent, field)),
  );
  const gridOfftakeBandOpen = !isWizard || gridOfftakeHasValue;
  const gridOfftakeBand = (
    <details
      className="border border-border-subtle rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-grid-offtake-band`}
      data-field-band="grid-offtake"
      {...(gridOfftakeBandOpen ? { open: true } : {})}
    >
      <summary
        className="cursor-pointer px-3 py-2 type-meta text-text-secondary hover:bg-state-hover font-bold"
        data-testid={`${testIdPrefix}-grid-offtake-toggle`}
      >
        {isWizard ? '+ Grid & offtake (refine on site page)' : 'Grid & offtake'}
      </summary>
      <div
        className="px-3 pb-3 pt-2 border-t border-border-subtle space-y-3"
        data-testid={`${testIdPrefix}-grid-offtake-fields`}
      >
        <FieldBandHeader
          label="Grid interconnection & offtake"
          helper="Record the utility service or generator queue milestone and how power is sold. Agreement acceptance and energization are separate milestones."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1" data-field-id="interconnectionStatus">
            <div className="type-meta text-text-muted">Utility service / interconnection status</div>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'interconnectionStatus')
                ? EXPLICIT_UNKNOWN_SELECT_VALUE
                : (intent.interconnectionStatus?.queueStatus ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  interconnectionStatus: !raw || raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                    ? undefined
                    : {
                      ...intent.interconnectionStatus,
                      queueStatus: raw as NonNullable<ProjectIntent['interconnectionStatus']>['queueStatus'],
                    },
                };
                onChange(raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                  ? markIntentFieldExplicitlyUnknown(next, 'interconnectionStatus')
                  : clearIntentFieldExplicitlyUnknown(next, 'interconnectionStatus'));
              }}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-interconnectionStatus`}
            >
              <option value="">(not set)</option>
              <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
              <option value="none">No request yet</option>
              <option value="pre-application">Pre-application / scoping</option>
              <option value="queued">In queue</option>
              <option value="study">Study underway</option>
              <option value="executed-LGIA">Executed generator LGIA</option>
              <option value="executed-load-service">Load-service agreement accepted</option>
            </select>
          </div>
          <div className="space-y-1" data-field-id="interconnectionStatusQueueId">
            <div className="type-meta text-text-muted">Utility application / queue ID (optional)</div>
            <input
              type="text"
              placeholder="e.g. Q2034"
              value={intent.interconnectionStatus?.queueId ?? ''}
              onChange={(e) => onChange(clearIntentFieldExplicitlyUnknown({
                ...intent,
                interconnectionStatus: {
                  queueStatus: intent.interconnectionStatus?.queueStatus ?? 'none',
                  ...intent.interconnectionStatus,
                  queueId: e.target.value || undefined,
                },
              }, 'interconnectionStatus'))}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-interconnectionStatus-queueId`}
            />
          </div>
          <div className="space-y-1" data-field-id="fuelAccess">
            <div className="type-meta text-text-muted">Gas / fuel access</div>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'fuelAccess')
                ? EXPLICIT_UNKNOWN_SELECT_VALUE
                : (intent.fuelAccess?.supply ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  fuelAccess: !raw || raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                    ? undefined
                    : {
                      ...intent.fuelAccess,
                      supply: raw as NonNullable<ProjectIntent['fuelAccess']>['supply'],
                    },
                };
                onChange(raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                  ? markIntentFieldExplicitlyUnknown(next, 'fuelAccess')
                  : clearIntentFieldExplicitlyUnknown(next, 'fuelAccess'));
              }}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-fuelAccess`}
            >
              <option value="">(not set)</option>
              <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
              <option value="none">No gas access</option>
              <option value="near-pipeline">Near pipeline</option>
              <option value="lateral-required">Lateral required</option>
              <option value="firm-contracted">Firm transport contracted</option>
            </select>
          </div>
          <div className="space-y-1" data-field-id="offtake">
            <div className="type-meta text-text-muted">Offtake / PPA structure</div>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'offtake')
                ? EXPLICIT_UNKNOWN_SELECT_VALUE
                : (intent.offtake?.structure ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                let next: ProjectIntent = {
                  ...intent,
                  offtake: !raw || raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                    ? undefined
                    : {
                      ...intent.offtake,
                      structure: raw as NonNullable<ProjectIntent['offtake']>['structure'],
                    },
                };
                if (!next.offtake) {
                  next = clearIntentFieldExplicitlyUnknown(next, 'offtake.priceBasis');
                }
                onChange(raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                  ? markIntentFieldExplicitlyUnknown(next, 'offtake')
                  : clearIntentFieldExplicitlyUnknown(next, 'offtake'));
              }}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-offtake`}
            >
              <option value="">(not set)</option>
              <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
              <option value="merchant">Merchant</option>
              <option value="contracted">Contracted PPA</option>
              <option value="hybrid">Hybrid</option>
            </select>
            {renderSuggestionChip('offtake')}
          </div>
          <div className="space-y-1" data-field-id="capacityCreditIntent">
            <div className="type-meta text-text-muted">Capacity-credit (ELCC) intent</div>
            <select
              value={isIntentFieldExplicitlyUnknown(intent, 'capacityCreditIntent')
                ? EXPLICIT_UNKNOWN_SELECT_VALUE
                : (intent.capacityCreditIntent?.target ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const next: ProjectIntent = {
                  ...intent,
                  capacityCreditIntent: !raw || raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                    ? undefined
                    : {
                      ...intent.capacityCreditIntent,
                      target: raw as NonNullable<ProjectIntent['capacityCreditIntent']>['target'],
                    },
                };
                onChange(raw === EXPLICIT_UNKNOWN_SELECT_VALUE
                  ? markIntentFieldExplicitlyUnknown(next, 'capacityCreditIntent')
                  : clearIntentFieldExplicitlyUnknown(next, 'capacityCreditIntent'));
              }}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-capacityCreditIntent`}
            >
              <option value="">(not set)</option>
              <option value={EXPLICIT_UNKNOWN_SELECT_VALUE}>Unknown / not yet decided</option>
              <option value="none">No firm-capacity sought</option>
              <option value="partial">Partial accreditation</option>
              <option value="firm">Full firm accreditation</option>
            </select>
            {renderSuggestionChip('capacityCreditIntent')}
          </div>
          <ElectricitySupplyField intent={intent} onChange={onChange} testId={`${testIdPrefix}-electricitySupply`} unknownValue={EXPLICIT_UNKNOWN_SELECT_VALUE} />
        </div>
      </div>
    </details>
  );

  /* ── Site control & resources band (P1 — audit §3.6/§4) ──
   * The BTM-feasibility intent field concerned with the physical site:
   * waterSource (source + rights). The `landControl` select that used to sit
   * here was removed (intent-input audit — prompt-only, never scored or
   * stamped to the SED). Band test-ids keep the `site-control` prefix so the
   * stepper / existing tests are undisturbed. Same idiom as the grid-&-offtake
   * band. */
  const siteControlHasValue = Boolean(
    hasDeclaredWaterSource(intent.waterSource)
    || isIntentFieldExplicitlyUnknown(intent, 'waterSource'),
  );
  const siteControlBandOpen = !isWizard || siteControlHasValue;
  const siteControlBand = (
    <details
      className="border border-border-subtle rounded-lg overflow-hidden"
      data-testid={`${testIdPrefix}-site-control-band`}
      data-field-band="site-control-resources"
      {...(siteControlBandOpen ? { open: true } : {})}
    >
      <summary
        className="cursor-pointer px-3 py-2 type-meta text-text-secondary hover:bg-state-hover font-bold"
        data-testid={`${testIdPrefix}-site-control-toggle`}
      >
        {isWizard ? '+ Water source & rights (refine on site page)' : 'Water source & rights'}
      </summary>
      <div
        className="px-3 pb-3 pt-2 border-t border-border-subtle space-y-3"
        data-testid={`${testIdPrefix}-site-control-fields`}
      >
        <FieldBandHeader
          label="Water source & rights"
          helper="The water source / rights that feed the cooling-water gate. Feeds the water screen in the Early Screening Assessment."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1" data-field-id="waterSource">
            <div className="type-meta text-text-muted">Water source <span className="text-text-muted/70">(select all that apply)</span></div>
            <div
              className="flex flex-col gap-1 rounded-md border border-border bg-surface p-2"
              role="group"
              aria-label="Water source"
              data-testid={`${testIdPrefix}-waterSource`}
            >
              {WATER_SOURCE_OPTIONS.map(({ value, label }) => {
                const selected = waterSourceKinds(intent.waterSource);
                const checked = selected.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 text-sm text-text-primary cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = checked
                          ? selected.filter((k) => k !== value)
                          : [...selected, value];
                        // Strip the legacy scalar on write so a fully-cleared
                        // multi-select never resurfaces an old `source` value.
                        const { source: _legacy, ...rest } = intent.waterSource ?? {};
                        onChange(clearIntentFieldExplicitlyUnknown({
                          ...intent,
                          waterSource: next.length === 0 && rest.rightsStatus == null
                            ? undefined
                            : { ...rest, sources: next },
                        }, 'waterSource'));
                      }}
                      className="h-4 w-4 accent-accent"
                      data-testid={`${testIdPrefix}-waterSource-${value}`}
                    />
                    {label}
                  </label>
                );
              })}
              <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isIntentFieldExplicitlyUnknown(intent, 'waterSource')}
                  onChange={(event) => onChange(event.target.checked
                    ? markIntentFieldExplicitlyUnknown(
                      { ...intent, waterSource: undefined },
                      'waterSource',
                    )
                    : clearIntentFieldExplicitlyUnknown(intent, 'waterSource'))}
                  className="h-4 w-4 accent-accent"
                  data-testid={`${testIdPrefix}-waterSource-unknown`}
                />
                Unknown / not yet decided
              </label>
            </div>
          </div>
          <div className="space-y-1" data-field-id="waterSourceRights">
            <div className="type-meta text-text-muted">Water rights status (optional)</div>
            <select
              value={intent.waterSource?.rightsStatus ?? ''}
              onChange={(e) => onChange({
                ...intent,
                waterSource: {
                  ...intent.waterSource,
                  rightsStatus: (e.target.value || undefined) as NonNullable<ProjectIntent['waterSource']>['rightsStatus'],
                },
              })}
              className="w-full h-9 px-2 border border-border bg-surface text-text-primary text-sm rounded-md"
              data-testid={`${testIdPrefix}-waterSource-rightsStatus`}
            >
              <option value="">(not set)</option>
              <option value="none">No rights</option>
              <option value="applied">Applied for</option>
              <option value="permitted">Permitted</option>
            </select>
          </div>
        </div>
      </div>
    </details>
  );

  /* ── Cross-field warnings ─────────────────────────────────────────
   * BriefMismatchWarnings becomes a `warnings` prop in NSW2. Render
   * them inline beneath the form so users see the conflict next to the
   * chip they need to flip. Same chip primitive the prefs forms use so
   * the design language stays consistent. */
  const warningChips = (warnings && warnings.length > 0) ? (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-cross-field-warnings`}>
      {warnings.map((w, i) => (
        <div key={`${w.field}-${i}`} data-testid={`${testIdPrefix}-cross-field-warning`}>
          <CrossFieldWarningChip warning={w} onApplyFix={onApplyWarningFix} />
        </div>
      ))}
    </div>
  ) : null;

  /* ── AI context preview REMOVED (Declutter, 2026-06-10) ────────────
   * The "AI context preview" card was removed per William — it duplicated
   * the SiteConfigurationPanel collapsed-header summary and added clutter.
   * The `showAiContextPreview` prop stays on the interface for caller
   * back-compat (it is simply no longer consumed; see `effectiveShowAiPreview`
   * below, kept inert to avoid changing the prop contract). */

  /* ── Focused-Stepper layout (opt-in) ──────────────────────────────
   * PRESENTATION-ONLY chrome around the SAME extracted field groups. No
   * field, writer, conditional, chip or notice differs from scroll mode
   * — only the wrapper. Each step's content stays MOUNTED and is gated
   * with the `hidden` attribute (never conditionally unmounted) so field
   * state + the always-mounted Intelligence banner survive navigation.
   *
   * Group → step mapping (the 3 extra `<details>` bands that fall
   * outside the 7-step spec are folded into the closest-related step so
   * NO field is dropped):
   *   power    → power requirement + basis/PUE  +  grid & offtake  +  site control & water
   *   workload → workload profile
   *   phasing  → phased rollout editor
   *   cooling  → IT loop + BTM cooling axes
   *   timeline → in-service date / horizon
   *   nuclear  → nuclear interest (+ conditional reactor design / vendor)
   *   priority → priority  +  project profile  +  operator attestations */
  const screeningModeControl = (
    <ScreeningApproachPicker
      value={isScreeningRecommendationMode(value) ? 'recommend' : 'evaluate-proposal'}
      disabled={readOnly || resetPending || resetDisabled}
      testIdPrefix={testIdPrefix}
      onChange={choice => {
        if (readOnly || resetPending || resetDisabled) return;
        onChange(withScreeningIntentMode(value, choice));
      }}
    />
  );

  if (isScreening && isScreeningRecommendationMode(value)) {
    return (
      <div className="space-y-5" data-testid={testIdPrefix} data-form-mode={mode} data-layout="screening">
        <header className="space-y-1" data-testid={`${testIdPrefix}-brief-intro`}>
          <h2 className="text-base font-semibold text-text-primary">Project brief</h2>
          <p className="text-sm text-text-secondary">Start with what you know. Screening can recommend the rest.</p>
        </header>
        {screeningModeControl}
        {contextStep != null && <section className="space-y-3" aria-label="Site context">{contextStep}</section>}
        <p className="text-sm text-text-secondary" data-testid={`${testIdPrefix}-screening-recommend-mode`}>
          Next, run Screening from Analyses. Review its current recommendations here before applying them to Intent, Stage 2 or Generation &amp; Impact. Engineering unknowns remain unresolved.
        </p>
        {onOpenDetailedPlanning && <Button type="button" variant="ghost" onClick={onOpenDetailedPlanning}
          data-testid={`${testIdPrefix}-open-design`}>Review Stage 2 design choices</Button>}
        {onStepperDone && <Button type="button" onClick={onStepperDone} data-testid={`${testIdPrefix}-screening-done`}>Done</Button>}
      </div>
    );
  }

  if (isScreening) {
    return (
      <div className="space-y-5" data-testid={testIdPrefix} data-form-mode={mode} data-layout="screening">
        <header className="space-y-1" data-testid={`${testIdPrefix}-brief-intro`}>
          <h2 className="text-base font-semibold text-text-primary">Project brief</h2>
          <p className="text-sm text-text-secondary">Start with what you know. Screening can recommend the rest.</p>
        </header>
        {screeningModeControl}
        {contextStep != null && (
          <section className="space-y-3" aria-label="Site context">{contextStep}</section>
        )}
        {workloadGroup}
        {powerGroup}
        {timelineGroup}
        <ScreeningSharpenFields intent={intent} onChange={onChange} testIdPrefix={testIdPrefix} />
        <div className="rounded-lg border border-border-subtle p-3 space-y-2" data-testid={`${testIdPrefix}-recommendation-note`}>
          <p className="text-[11px] text-text-muted">
            The screening report compares power supply and cooling options, then recommends an approach for this site.
            You do not need to choose a technology, cooling system or nuclear design here.
          </p>
          {onOpenDetailedPlanning && <Button type="button" variant="ghost" onClick={onOpenDetailedPlanning}
            data-testid={`${testIdPrefix}-open-design`}>Review design &amp; report assumptions</Button>}
        </div>
        {warningChips}
        {onStepperDone && <Button type="button" onClick={onStepperDone} data-testid={`${testIdPrefix}-screening-done`}>Done</Button>}
      </div>
    );
  }

  if (stepper) {
    // Declutter — PREPEND the Context step ONLY when the host handed us a
    // `contextStep` node. Otherwise the rail is exactly the canonical 7
    // field steps (back-compat for wizard / rerun / parity tests).
    const effectiveSteps: readonly StepperStep[] = contextStep != null
      ? [CONTEXT_STEP, ...STEPPER_STEPS]
      : STEPPER_STEPS;

    // Context completeness is host-driven (`contextStepComplete`); every
    // other step reads its real intent field via `stepperStepIsComplete`.
    const isStepComplete = (id: StepperStepId): boolean =>
      id === 'context' ? contextStepComplete : stepperStepIsComplete(id, intent);

    const activeIndex = effectiveSteps.findIndex(s => s.id === activeStep);
    const safeIndex = activeIndex < 0 ? 0 : activeIndex;
    const activeMeta = effectiveSteps[safeIndex];
    const isFirst = safeIndex === 0;
    const isLast = safeIndex === effectiveSteps.length - 1;
    const completeCount = effectiveSteps.filter(s => isStepComplete(s.id)).length;
    const totalSteps = effectiveSteps.length;

    const goTo = (id: StepperStepId) => setActiveStep(id);
    const goNext = () => {
      if (!isLast) setActiveStep(effectiveSteps[safeIndex + 1].id);
    };
    const goBack = () => {
      if (!isFirst) setActiveStep(effectiveSteps[safeIndex - 1].id);
    };

    /** Per-step content — same elements as scroll mode, plus the folded
     *  extra bands. Each block is kept mounted and shown/hidden via the
     *  `hidden` attribute so React never tears down field state. The
     *  `context` panel renders the host-supplied node (only present when
     *  `contextStep` was passed, so it lands in `effectiveSteps`). */
    const stepContent: Record<StepperStepId, React.ReactNode> = {
      context: contextStep ?? null,
      power: (
        <div className="space-y-4">
          {powerGroup}
          {gridOfftakeBand}
        </div>
      ),
      workload: workloadGroup,
      phasing: phasesGroup,
      cooling: (
        <div className="space-y-4">
          {coolingGroup}
          {siteControlBand}
        </div>
      ),
      timeline: timelineGroup,
      nuclear: <div className="space-y-3">{nuclearGroup}</div>,
      priority: (
        <div className="space-y-4">
          {optionalBandInner}
          {expandedProfileBand}
          {attestationsBand}
        </div>
      ),
    };

    return (
      <div className="space-y-4" data-testid={testIdPrefix} data-form-mode={mode} data-layout="stepper">
        {/* Intent Intelligence — the top "Suggested next" nudge is reinstated
            behind `isIntentNextBestInputEnabled()` (default OFF → null →
            byte-identical to the decluttered form). Per-field suggestion chips
            + disagreement notices remain inside each step's group regardless. */}
        {nextInputNudge}

        {/* Step navigation stays visible and wraps on narrow screens. */}
        <div className="space-y-2">
          <div
            role="tablist"
            aria-label="Project intent steps"
            className="flex flex-wrap items-center gap-x-1 gap-y-2 pb-1"
            data-testid={`${testIdPrefix}-stepper-rail`}
          >
            {effectiveSteps.map((step, i) => {
              const isActive = step.id === activeStep;
              const done = isStepComplete(step.id);
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  {i > 0 && (
                    <span
                      aria-hidden="true"
                      className="h-px w-3 shrink-0 bg-border-subtle"
                    />
                  )}
                  <button
                    type="button"
                    role="tab"
                    id={`${testIdPrefix}-step-tab-${step.id}`}
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    aria-controls={`${testIdPrefix}-step-panel-${step.id}`}
                    aria-label={`Step ${i + 1} of ${totalSteps}: ${step.label}${done ? ' (complete)' : ''}`}
                    onClick={() => goTo(step.id)}
                    onKeyDown={event => {
                      const targetIndex = event.key === 'ArrowRight' ? (i + 1) % totalSteps
                        : event.key === 'ArrowLeft' ? (i + totalSteps - 1) % totalSteps
                        : event.key === 'Home' ? 0 : event.key === 'End' ? totalSteps - 1 : null;
                      if (targetIndex == null) return;
                      event.preventDefault();
                      goTo(effectiveSteps[targetIndex].id);
                      event.currentTarget.closest('[role="tablist"]')
                        ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[targetIndex]?.focus();
                    }}
                    data-testid={`${testIdPrefix}-step-chip-${step.id}`}
                    data-active={isActive ? 'true' : 'false'}
                    data-complete={done ? 'true' : 'false'}
                    className={`group inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                      isActive
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-border-subtle text-text-secondary hover:border-border hover:text-text-primary'
                    }`}
                  >
                    <span
                      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${
                        done
                          ? 'border-primary bg-primary text-primary-foreground'
                          : isActive
                            ? 'border-primary text-primary'
                            : 'border-border-subtle text-text-muted'
                      }`}
                    >
                      {done ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="type-meta whitespace-nowrap">{step.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {/* Completeness count + progress bar. */}
          <div className="flex items-center gap-3">
            <span
              className="type-meta text-text-muted whitespace-nowrap"
              data-testid={`${testIdPrefix}-stepper-progress-count`}
            >
              {completeCount}/{totalSteps} complete
            </span>
            <div
              className="h-1 flex-1 overflow-hidden rounded-full bg-border-subtle"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={totalSteps}
              aria-valuenow={completeCount}
              data-testid={`${testIdPrefix}-stepper-progress-bar`}
            >
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${(completeCount / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* All panels keep their identity while navigating, including host drafts. */}
        <div
          className="rounded-xl border border-border bg-card p-4 sm:p-5"
          data-testid={`${testIdPrefix}-stepper-card`}
        >
          <div className="space-y-1 mb-4">
            <div className="type-meta text-text-muted">Step {safeIndex + 1} of {totalSteps}</div>
            <div className="text-lg font-bold text-text-primary">{activeMeta.label}</div>
            <div className="text-xs text-text-secondary leading-relaxed">{activeMeta.purpose}</div>
          </div>

          {renderStepStageHeader && (
            <div className="mb-4" data-testid={`${testIdPrefix}-stepper-stage-header`}>
              {renderStepStageHeader({ activeStep, onSelectStep: goTo })}
            </div>
          )}

          {effectiveSteps.map(step => (
            <div
              key={step.id}
              role="tabpanel"
              id={`${testIdPrefix}-step-panel-${step.id}`}
              aria-labelledby={`${testIdPrefix}-step-tab-${step.id}`}
              hidden={step.id !== activeStep}
              data-testid={`${testIdPrefix}-step-panel-${step.id}`}
            >
              {stepContent[step.id]}
            </div>
          ))}
        </div>

        {/* FOOTER — Back / Next. Never auto-saves; the host owns Save. */}
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            className="h-9 rounded-md type-meta"
            onClick={goBack}
            disabled={isFirst}
            data-testid={`${testIdPrefix}-stepper-back`}
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back
          </Button>
          <Button
            className="h-9 rounded-md type-meta"
            onClick={() => {
              if (isLast) onStepperDone?.();
              else goNext();
            }}
            // "Done" is live when the host wires a destination (the guided
            // loop's return-to-hub); disabled only for legacy hosts without one.
            disabled={isLast && !onStepperDone}
            data-testid={`${testIdPrefix}-stepper-next`}
          >
            {isLast ? 'Done' : 'Next'}
            {!isLast && <ArrowRight className="h-3.5 w-3.5 ml-1" />}
          </Button>
        </div>

        {warningChips}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={testIdPrefix} data-form-mode={mode}>
      {nextInputNudge}
      {scoreAffectingBand}
      {recommendedBand}
      {optionalBand}
      {attestationsBand}
      {expandedProfileBand}
      {gridOfftakeBand}
      {siteControlBand}
      {warningChips}
    </div>
  );
};

/** Count of fields that are NOT undecided / undefined. Drives the "Set" status chip. */
export function countSetIntentFields(intent: ProjectIntent | undefined): number {
  if (!intent) return 0;
  let n = 0;
  if (intent.workloadProfile && intent.workloadProfile !== 'mixed') n++;
  if (intent.powerRequirement && intent.powerRequirement !== 'undecided') n++;
  if (intent.coolingStrategy && intent.coolingStrategy !== 'undecided') n++;
  if (intent.nuclearInterest && intent.nuclearInterest !== 'undecided') n++;
  if (intent.reactorDesignClass && intent.reactorDesignClass !== 'undecided') n++;
  if (intent.timeline && intent.timeline !== 'undecided') n++;
  if (intent.priority && intent.priority !== 'undecided') n++;
  return n;
}
